/**
 * GENERATOR V3.0 — DAW-GRADE BACKING TRACK ENGINE
 * High-performance arrangement architect with reactive drums, synth pad sounds,
 * walking bass, groove library, hash save/recall, and MIDI export.
 */

class GeneratorEngineV20 {
    constructor() {
        this.audioReady = false;
        this.isPlaying = false;
        this.bpm = 110;
        this.currentBar = 0;
        this.barsPlayCount = 0;
        this.loopId = null;
        this.progressionLength = 8;
        this.tapTempoTimes = [];
        this.statusTimer = null;
        this.isInitialized = false;
        this.dailyStorageKey = 'ashe_generator_daily_state_v1';
        this.settingsKey = 'sa-tool-generator';

        // Synths & FX
        this.synths = {};
        this.drumKit = {};
        this.fx = {};

        // Mix levels (dB)
        this.levels = { pad: -8, bass: -6, drums: -6 };

        // Session state
        this.key = "C";
        this.mode = "ionian";
        this.style = "prog_rock";
        this.padSound = "warm_pad"; // warm_pad, electric_piano, strings, glass, organ, brass
        this.rhythmStyle = "strum"; // strum, arpeggio, swell, simple
        this.harmonicIntent = "stable"; // stable, sophisticated, pro
        this.enableVoiceLeading = true;
        this.autoInvert = false;
        this.progression = [];
        this.lastPlayedNotes = null;

        // Metronome State
        this.metronomeActive = false;
        this.swingAmount = 0;
        this.timeSignature = [4, 4];

        // Theory Library
        this.theoryMapping = {
            'Maj7': { scales: ['Ionian', 'Lydian'], roman: 'I' },
            'min7': { scales: ['Dorian', 'Aeolian', 'Phrygian'], roman: 'ii' },
            'dom7': { scales: ['Mixolydian', 'Lydian b7'], roman: 'V' },
            'ø7': { scales: ['Locrian', 'Locrian #2'], roman: 'vii°' },
            'dim7': { scales: ['Diminished'], roman: 'vii°' },
            'Maj': { scales: ['Major Pentatonic'], roman: 'I' },
            'min': { scales: ['Minor Pentatonic'], roman: 'i' }
        };

        this.library = {
            pop_1564: [
                { root: 'C', quality: 'Maj7', bars: 2, degree: 1 },
                { root: 'G', quality: 'dom7', bars: 2, degree: 5 },
                { root: 'A', quality: 'min7', bars: 2, degree: 6 },
                { root: 'F', quality: 'Maj7', bars: 2, degree: 4 }
            ],
            axis: [
                { root: 'A', quality: 'min7', bars: 2, degree: 6 },
                { root: 'F', quality: 'Maj7', bars: 2, degree: 4 },
                { root: 'C', quality: 'Maj7', bars: 2, degree: 1 },
                { root: 'G', quality: 'dom7', bars: 2, degree: 5 }
            ],
            canon: [
                { root: 'D', quality: 'Maj7', bars: 2, degree: 1 },
                { root: 'A', quality: 'dom7', bars: 2, degree: 5 },
                { root: 'B', quality: 'min7', bars: 2, degree: 6 },
                { root: 'F#', quality: 'min7', bars: 2, degree: 3 },
                { root: 'G', quality: 'Maj7', bars: 2, degree: 4 },
                { root: 'D', quality: 'Maj7', bars: 2, degree: 1 },
                { root: 'G', quality: 'Maj7', bars: 2, degree: 4 },
                { root: 'A', quality: 'dom7', bars: 2, degree: 5 }
            ],
            blues_12: [
                { root: 'C', quality: 'dom7', bars: 1, degree: 1 },
                { root: 'F', quality: 'dom7', bars: 1, degree: 4 },
                { root: 'C', quality: 'dom7', bars: 1, degree: 1 },
                { root: 'C', quality: 'dom7', bars: 1, degree: 1 },
                { root: 'F', quality: 'dom7', bars: 2, degree: 4 },
                { root: 'C', quality: 'dom7', bars: 2, degree: 1 },
                { root: 'G', quality: 'dom7', bars: 1, degree: 5 },
                { root: 'F', quality: 'dom7', bars: 1, degree: 4 },
                { root: 'C', quality: 'dom7', bars: 2, degree: 1 }
            ],
            jazz_251: [
                { root: 'D', quality: 'min7', bars: 2, degree: 2 },
                { root: 'G', quality: 'dom7', bars: 2, degree: 5 },
                { root: 'C', quality: 'Maj7', bars: 4, degree: 1 }
            ],
            autumn_leaves: [
                { root: 'A', quality: 'min7', bars: 2, degree: 2 },
                { root: 'D', quality: 'dom7', bars: 2, degree: 5 },
                { root: 'G', quality: 'Maj7', bars: 2, degree: 1 },
                { root: 'C', quality: 'Maj7', bars: 2, degree: 4 },
                { root: 'F#', quality: 'ø7', bars: 2, degree: 7 },
                { root: 'B', quality: 'dom7', bars: 2, degree: 3 },
                { root: 'E', quality: 'min7', bars: 4, degree: 6 }
            ],
            blue_bossa: [
                { root: 'C', quality: 'min7', bars: 4, degree: 1 },
                { root: 'F', quality: 'min7', bars: 2, degree: 4 },
                { root: 'D', quality: 'ø7', bars: 2, degree: 2 },
                { root: 'G', quality: 'dom7', bars: 2, degree: 5 },
                { root: 'C', quality: 'min7', bars: 2, degree: 1 },
                { root: 'Eb', quality: 'min7', bars: 1, degree: 3 },
                { root: 'Ab', quality: 'dom7', bars: 1, degree: 6 },
                { root: 'Db', quality: 'Maj7', bars: 2, degree: 2 }
            ],
            rhythm_changes: [
                { root: 'Bb', quality: 'Maj7', bars: 1, degree: 1 },
                { root: 'G', quality: 'dom7', bars: 1, degree: 6 },
                { root: 'C', quality: 'min7', bars: 1, degree: 2 },
                { root: 'F', quality: 'dom7', bars: 1, degree: 5 },
                { root: 'Bb', quality: 'Maj7', bars: 1, degree: 1 },
                { root: 'G', quality: 'dom7', bars: 1, degree: 6 },
                { root: 'C', quality: 'min7', bars: 1, degree: 2 },
                { root: 'F', quality: 'dom7', bars: 1, degree: 5 }
            ],
            stella_by: [
                { root: 'E', quality: 'ø7', bars: 2, degree: 7 },
                { root: 'A', quality: 'dom7', bars: 2, degree: 3 },
                { root: 'C', quality: 'min7', bars: 2, degree: 2 },
                { root: 'F', quality: 'dom7', bars: 2, degree: 5 },
                { root: 'F', quality: 'min7', bars: 2, degree: 4 },
                { root: 'Bb', quality: 'dom7', bars: 2, degree: 7 },
                { root: 'Eb', quality: 'Maj7', bars: 2, degree: 1 }
            ],
            modal_dorian: [
                { root: 'D', quality: 'min7', bars: 4, degree: 2 },
                { root: 'G', quality: 'dom7', bars: 4, degree: 5 }
            ],
            giant_steps: [
                { root: 'B', quality: 'Maj7', bars: 1, degree: 1 },
                { root: 'D', quality: 'dom7', bars: 1, degree: 3 },
                { root: 'G', quality: 'Maj7', bars: 1, degree: 6 },
                { root: 'Bb', quality: 'dom7', bars: 1, degree: 1 },
                { root: 'Eb', quality: 'Maj7', bars: 1, degree: 4 },
                { root: 'A', quality: 'min7', bars: 1, degree: 7 },
                { root: 'D', quality: 'dom7', bars: 1, degree: 3 },
                { root: 'G', quality: 'Maj7', bars: 1, degree: 6 }
            ],
            all_things: [
                { root: 'F', quality: 'Maj7', bars: 2, degree: 1 },
                { root: 'F', quality: 'min7', bars: 1, degree: 4 },
                { root: 'Bb', quality: 'dom7', bars: 1, degree: 7 },
                { root: 'Eb', quality: 'Maj7', bars: 2, degree: 4 },
                { root: 'Ab', quality: 'min7', bars: 1, degree: 3 },
                { root: 'Db', quality: 'dom7', bars: 1, degree: 6 },
                { root: 'Gb', quality: 'Maj7', bars: 2, degree: 2 },
                { root: 'E', quality: 'min7', bars: 1, degree: 7 },
                { root: 'A', quality: 'dom7', bars: 1, degree: 3 },
                { root: 'D', quality: 'Maj7', bars: 2, degree: 6 }
            ],
            so_what: [
                { root: 'D', quality: 'min7', bars: 8, degree: 2 },
                { root: 'Eb', quality: 'min7', bars: 8, degree: 3 },
                { root: 'D', quality: 'min7', bars: 8, degree: 2 }
            ],
            misty: [
                { root: 'Eb', quality: 'Maj7', bars: 2, degree: 1 },
                { root: 'Bb', quality: 'min7', bars: 1, degree: 5 },
                { root: 'Eb', quality: 'dom7', bars: 1, degree: 1 },
                { root: 'Ab', quality: 'Maj7', bars: 2, degree: 4 },
                { root: 'Ab', quality: 'min7', bars: 1, degree: 4 },
                { root: 'Db', quality: 'dom7', bars: 1, degree: 7 },
                { root: 'Eb', quality: 'Maj7', bars: 2, degree: 1 },
                { root: 'C', quality: 'min7', bars: 1, degree: 6 },
                { root: 'F', quality: 'dom7', bars: 1, degree: 2 },
                { root: 'F', quality: 'min7', bars: 1, degree: 2 },
                { root: 'Bb', quality: 'dom7', bars: 1, degree: 5 }
            ],
            phrygian_dark: [
                { root: 'E', quality: 'min7', bars: 4, degree: 1 },
                { root: 'F', quality: 'Maj7', bars: 4, degree: 2 }
            ],
            andalusian: [
                { root: 'A', quality: 'min', bars: 2, degree: 1 },
                { root: 'G', quality: 'Maj', bars: 2, degree: 7 },
                { root: 'F', quality: 'Maj', bars: 2, degree: 6 },
                { root: 'E', quality: 'dom7', bars: 2, degree: 5 }
            ],
            coltrane: [
                { root: 'C', quality: 'Maj7', bars: 1, degree: 1 },
                { root: 'Ab', quality: 'dom7', bars: 1, degree: 6 },
                { root: 'Db', quality: 'Maj7', bars: 1, degree: 2 },
                { root: 'A', quality: 'dom7', bars: 1, degree: 6 },
                { root: 'D', quality: 'Maj7', bars: 1, degree: 2 },
                { root: 'Bb', quality: 'dom7', bars: 1, degree: 7 },
                { root: 'Eb', quality: 'Maj7', bars: 1, degree: 3 },
                { root: 'C#', quality: 'dom7', bars: 1, degree: 1 }
            ],
            gospel: [
                { root: 'C', quality: 'Maj7', bars: 2, degree: 1 },
                { root: 'C', quality: 'dom7', bars: 1, degree: 1 },
                { root: 'F', quality: 'Maj7', bars: 1, degree: 4 },
                { root: 'F', quality: 'min7', bars: 1, degree: 4 },
                { root: 'C', quality: 'Maj7', bars: 1, degree: 1 },
                { root: 'A', quality: 'dom7', bars: 1, degree: 6 },
                { root: 'D', quality: 'min7', bars: 1, degree: 2 },
                { root: 'G', quality: 'dom7', bars: 2, degree: 5 }
            ],
            seven_eight_prog: [
                { root: 'E', quality: 'min7', bars: 2, degree: 1 },
                { root: 'G', quality: 'Maj7', bars: 2, degree: 3 },
                { root: 'A', quality: 'dom7', bars: 2, degree: 4 },
                { root: 'B', quality: 'min7', bars: 2, degree: 5 }
            ],
            five_four_take: [
                { root: 'Eb', quality: 'min7', bars: 2, degree: 1 },
                { root: 'Bb', quality: 'min7', bars: 2, degree: 5 },
                { root: 'Eb', quality: 'min7', bars: 2, degree: 1 },
                { root: 'Ab', quality: 'min7', bars: 2, degree: 4 }
            ]
        };

        // === SYNTH PAD SOUND CONFIGS ===
        this.padSoundConfigs = {
            warm_pad: {
                name: 'Warm Pad',
                oscillator: { type: 'fatsawtooth', count: 3, spread: 30 },
                filter: { type: 'lowpass', frequency: 2000, rolloff: -12 },
                envelope: { attack: 0.4, decay: 0.5, sustain: 0.8, release: 2.0 }
            },
            electric_piano: {
                name: 'Electric Piano',
                oscillator: { type: 'sine' },
                filter: { type: 'lowpass', frequency: 5000, rolloff: -12 },
                envelope: { attack: 0.01, decay: 0.8, sustain: 0.3, release: 1.2 }
            },
            strings: {
                name: 'Strings',
                oscillator: { type: 'fatsawtooth', count: 5, spread: 40 },
                filter: { type: 'lowpass', frequency: 3000, rolloff: -24 },
                envelope: { attack: 1.0, decay: 0.3, sustain: 0.9, release: 3.0 }
            },
            glass: {
                name: 'Glass / Bell',
                oscillator: { type: 'sine' },
                filter: { type: 'highpass', frequency: 800, rolloff: -12 },
                envelope: { attack: 0.001, decay: 1.5, sustain: 0.1, release: 2.5 }
            },
            organ: {
                name: 'Organ',
                oscillator: { type: 'square' },
                filter: { type: 'lowpass', frequency: 4000, rolloff: -12 },
                envelope: { attack: 0.01, decay: 0.1, sustain: 1.0, release: 0.1 }
            },
            brass: {
                name: 'Brass Section',
                oscillator: { type: 'sawtooth' },
                filter: { type: 'lowpass', frequency: 1500, rolloff: -12 },
                envelope: { attack: 0.15, decay: 0.3, sustain: 0.7, release: 0.5 }
            }
        };

        // === SAVED TRACKS (Hash System) ===
        this.savedTracks = this.loadSavedTracks();

        // Theory
        this.notes = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
        this.scales = {
            ionian: [0, 2, 4, 5, 7, 9, 11],
            aeolian: [0, 2, 3, 5, 7, 8, 10],
            dorian: [0, 2, 3, 5, 7, 9, 10],
            phrygian: [0, 1, 3, 5, 7, 8, 10],
            lydian: [0, 2, 4, 6, 7, 9, 11],
            mixolydian: [0, 2, 4, 5, 7, 9, 10],
            locrian: [0, 1, 3, 5, 6, 8, 10],
            melodic_minor: [0, 2, 3, 5, 7, 9, 11],
            harmonic_minor: [0, 2, 3, 5, 7, 8, 11],
            lydian_dominant: [0, 2, 4, 6, 7, 9, 10],
            altered: [0, 1, 3, 4, 6, 8, 10]
        };

        this.qualities = {
            ionian: ['Maj7', 'min7', 'min7', 'Maj7', 'dom7', 'min7', 'm7b5'],
            aeolian: ['min7', 'm7b5', 'Maj7', 'min7', 'min7', 'Maj7', 'dom7'],
            dorian: ['min7', 'min7', 'Maj7', 'dom7', 'min7', 'm7b5', 'Maj7'],
            phrygian: ['min7', 'Maj7', 'dom7', 'min7', 'm7b5', 'Maj7', 'min7'],
            lydian: ['Maj7', 'dom7', 'min7', 'm7b5', 'Maj7', 'min7', 'min7'],
            mixolydian: ['dom7', 'min7', 'm7b5', 'Maj7', 'min7', 'min7', 'Maj7'],
            locrian: ['m7b5', 'Maj7', 'min7', 'min7', 'Maj7', 'dom7', 'min7'],
            melodic_minor: ['min(maj7)', 'min7', 'Maj7#5', 'dom7', 'dom7', 'm7b5', 'm7b5'],
            harmonic_minor: ['min(maj7)', 'm7b5', 'Maj7#5', 'min7', 'dom7', 'Maj7', 'dim7']
        };

        // Style definitions - DISTINCT sounds for each
        this.styleConfigs = {
            prog_rock: {
                padType: 'fatsawtooth',
                padAttack: 0.3,
                padRelease: 1.5,
                bassType: 'sawtooth',
                drumPattern: 'rock',
                tempo: [100, 130],
                reverb: 0.3,
                reverbSend: 0.18,
                delaySend: 0.2,
                delayFeedback: 0.24,
                delayTime: '8n',
                padFilterHz: 2200,
                padDrive: 0.05,
                padChorus: 0.28,
                bassFilterHz: 1050,
                bassDrive: 0.12,
                drumToneHz: 11000,
                masterLow: 1,
                masterHigh: 1,
                timeHumanize: 1,
                velocityHumanize: 1
            },
            tech_metal: {
                padType: 'square',
                padAttack: 0.05,
                padRelease: 0.5,
                bassType: 'sawtooth',
                drumPattern: 'tech',
                tempo: [160, 200],
                reverb: 0.2,
                reverbSend: 0.08,
                delaySend: 0.08,
                delayFeedback: 0.12,
                delayTime: '16n',
                padFilterHz: 3200,
                padDrive: 0.14,
                padChorus: 0.1,
                bassFilterHz: 1600,
                bassDrive: 0.28,
                drumToneHz: 14000,
                masterLow: 2,
                masterHigh: 0,
                timeHumanize: 0.58,
                velocityHumanize: 0.7
            },
            ashe_fusion: {
                padType: 'fatsawtooth',
                padAttack: 0.1,
                padRelease: 2.0,
                bassType: 'sawtooth',
                drumPattern: 'fusion',
                tempo: [95, 125],
                reverb: 0.5,
                reverbSend: 0.2,
                delaySend: 0.3,
                delayFeedback: 0.32,
                delayTime: '8n.',
                padFilterHz: 2600,
                padDrive: 0.07,
                padChorus: 0.35,
                bassFilterHz: 1200,
                bassDrive: 0.16,
                drumToneHz: 10500,
                masterLow: 0,
                masterHigh: 2,
                timeHumanize: 1.18,
                velocityHumanize: 1.22
            },
            clean_fusion: {
                padType: 'sine',
                padAttack: 0.5,
                padRelease: 2.5,
                bassType: 'triangle',
                drumPattern: 'jazz',
                tempo: [85, 120],
                reverb: 0.4,
                reverbSend: 0.24,
                delaySend: 0.16,
                delayFeedback: 0.2,
                delayTime: '4n',
                padFilterHz: 4200,
                padDrive: 0.02,
                padChorus: 0.4,
                bassFilterHz: 900,
                bassDrive: 0.08,
                drumToneHz: 9500,
                masterLow: -1,
                masterHigh: 2,
                timeHumanize: 0.96,
                velocityHumanize: 1.06
            },
            atmospheric: {
                padType: 'fatsine',
                padAttack: 1.5,
                padRelease: 4.0,
                bassType: 'sine',
                drumPattern: 'ambient',
                tempo: [60, 90],
                reverb: 0.8,
                reverbSend: 0.35,
                delaySend: 0.42,
                delayFeedback: 0.46,
                delayTime: '4n.',
                padFilterHz: 1600,
                padDrive: 0.01,
                padChorus: 0.52,
                bassFilterHz: 650,
                bassDrive: 0.04,
                drumToneHz: 8000,
                masterLow: 1,
                masterHigh: 2,
                timeHumanize: 1.35,
                velocityHumanize: 0.82
            },
            djent: {
                padType: 'square',
                padAttack: 0.01,
                padRelease: 0.3,
                bassType: 'sawtooth',
                drumPattern: 'djent',
                tempo: [110, 150],
                reverb: 0.2,
                reverbSend: 0.06,
                delaySend: 0.05,
                delayFeedback: 0.08,
                delayTime: '16n',
                padFilterHz: 3600,
                padDrive: 0.18,
                padChorus: 0.08,
                bassFilterHz: 1700,
                bassDrive: 0.3,
                drumToneHz: 15000,
                masterLow: 3,
                masterHigh: -1,
                timeHumanize: 0.5,
                velocityHumanize: 0.72
            },
            cinematic: {
                padType: 'fatsawtooth',
                padAttack: 2.0,
                padRelease: 5.0,
                bassType: 'sine',
                drumPattern: 'epic',
                tempo: [70, 100],
                reverb: 0.6,
                reverbSend: 0.3,
                delaySend: 0.26,
                delayFeedback: 0.34,
                delayTime: '8n',
                padFilterHz: 1900,
                padDrive: 0.04,
                padChorus: 0.45,
                bassFilterHz: 800,
                bassDrive: 0.09,
                drumToneHz: 9800,
                masterLow: 2,
                masterHigh: 1,
                timeHumanize: 1.25,
                velocityHumanize: 0.9
            },
            neo_soul: {
                padType: 'fatsine',
                padAttack: 0.1,
                padRelease: 1.2,
                bassType: 'triangle',
                drumPattern: 'jazz',
                tempo: [75, 95],
                reverb: 0.5,
                reverbSend: 0.2,
                delaySend: 0.24,
                delayFeedback: 0.28,
                delayTime: '8n',
                padFilterHz: 3000,
                padDrive: 0.03,
                padChorus: 0.33,
                bassFilterHz: 900,
                bassDrive: 0.1,
                drumToneHz: 9000,
                masterLow: 0,
                masterHigh: 1,
                timeHumanize: 1.08,
                velocityHumanize: 1.12
            },
            jazz_swing: {
                padType: 'sine',
                padAttack: 0.05,
                padRelease: 0.8,
                bassType: 'triangle',
                drumPattern: 'jazz',
                tempo: [120, 180],
                reverb: 0.3,
                reverbSend: 0.14,
                delaySend: 0.12,
                delayFeedback: 0.16,
                delayTime: '8t',
                padFilterHz: 4200,
                padDrive: 0.01,
                padChorus: 0.22,
                bassFilterHz: 1100,
                bassDrive: 0.06,
                drumToneHz: 10000,
                masterLow: -1,
                masterHigh: 1,
                swing: 0.6,
                timeHumanize: 1.22,
                velocityHumanize: 1.28
            }
        };

        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Arrangement Architect (V20)
        this.arrangements = {
            standard: ['Intro', 'Verse', 'Chorus', 'Verse', 'Chorus', 'Bridge', 'Chorus', 'Outro'],
            jam: ['Main', 'Main', 'Variation', 'Main'],
            linear: ['A', 'B', 'B', 'A']
        };
        this.currentArrangement = 'jam';
    }

    // ========================================
    // HASH SAVE/RECALL SYSTEM
    // ========================================
    generateHash() {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let hash = '';
        for (let i = 0; i < 6; i++) hash += chars.charAt(Math.floor(Math.random() * chars.length));
        return hash;
    }

    cleanTrackName(name, fallback) {
        const cleaned = String(name || '')
            .replace(/[\u0000-\u001F\u007F]/g, '')
            .trim()
            .slice(0, 80);
        return cleaned || fallback;
    }

    saveTrack(name) {
        const hash = this.generateHash();
        const track = {
            hash,
            name: this.cleanTrackName(name, `${this.key} ${this.mode} Jam`),
            key: this.key,
            mode: this.mode,
            style: this.style,
            padSound: this.padSound,
            bpm: this.bpm,
            progressionLength: this.progressionLength,
            rhythmStyle: this.rhythmStyle,
            harmonicIntent: this.harmonicIntent,
            timeSignature: [...this.timeSignature],
            swingAmount: this.swingAmount,
            enableVoiceLeading: this.enableVoiceLeading,
            autoInvert: this.autoInvert,
            progression: JSON.parse(JSON.stringify(this.progression)),
            levels: { ...this.levels },
            savedAt: new Date().toISOString()
        };
        this.savedTracks[hash] = track;
        this.persistSavedTracks();
        return hash;
    }

    loadTrack(hash) {
        const track = this.savedTracks[hash];
        if (!track) return false;
        const progression = this.sanitizeProgression(track.progression);
        if (progression.length === 0) return false;

        this.key = track.key || 'C';
        this.mode = track.mode || 'ionian';
        this.style = this.styleConfigs[track.style] ? track.style : 'prog_rock';
        this.padSound = track.padSound || 'warm_pad';
        this.bpm = track.bpm || 110;
        this.progressionLength = Math.max(4, Math.min(16, track.progressionLength || track.progression?.length || this.progressionLength));
        this.rhythmStyle = track.rhythmStyle || 'strum';
        this.harmonicIntent = track.harmonicIntent || 'stable';
        this.timeSignature = track.timeSignature || [4, 4];
        this.swingAmount = track.swingAmount || 0;
        this.enableVoiceLeading = track.enableVoiceLeading !== false;
        this.autoInvert = track.autoInvert || false;
        this.progression = progression;
        this.recomputeChordDegrees();
        if (track.levels) this.levels = { ...track.levels };

        // Sync UI
        this.syncAllUI();
        this.renderChords();
        if (this.audioReady) {
            this.applyStyle();
            this.updateMix();
            this.rebuildPadSynth();
        }
        this.restartIfPlaying();
        return true;
    }

    deleteTrack(hash) {
        delete this.savedTracks[hash];
        this.persistSavedTracks();
    }

    loadSavedTracks() {
        try {
            const data = window.flPreferenceStorage.getItem('ashe_generator_tracks');
            return data ? JSON.parse(data) : {};
        } catch (e) { return {}; }
    }

    persistSavedTracks() {
        try {
            window.flPreferenceStorage.setItem('ashe_generator_tracks', JSON.stringify(this.savedTracks));
        } catch (e) { /* storage full */ }
    }

    getSavedTracksList() {
        return Object.values(this.savedTracks).sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));
    }

    normalizeNoteName(note) {
        const map = {
            Db: 'C#',
            Eb: 'D#',
            Gb: 'F#',
            Ab: 'G#',
            Bb: 'A#',
            Cb: 'B',
            Fb: 'E',
            'E#': 'F',
            'B#': 'C'
        };
        return map[note] || note;
    }

    sanitizeProgression(value) {
        if (!Array.isArray(value)) return [];
        const allowedQualities = new Set(Object.values(this.qualities).flat().concat(['Maj', 'min', 'ø7']));
        const allowedExtensions = new Set(['7', '9', '11', '13', 'b9', '#11']);

        return value.slice(0, 16).filter(chord => chord && typeof chord === 'object').map(chord => ({
            root: /^[A-G](?:#|b)?$/.test(String(chord.root || '')) ? String(chord.root) : this.key,
            quality: allowedQualities.has(chord.quality) ? chord.quality : 'Maj7',
            bars: Math.max(1, Math.min(8, Number(chord.bars) || 1)),
            inversion: Math.max(0, Math.min(3, Number(chord.inversion) || 0)),
            extensions: Array.isArray(chord.extensions)
                ? chord.extensions.map(String).filter(value => allowedExtensions.has(value)).slice(0, 4)
                : [],
            degree: Math.max(1, Math.min(7, Number(chord.degree) || 1))
        }));
    }

    getNoteIndex(note) {
        return this.notes.indexOf(this.normalizeNoteName(note));
    }

    transposeNoteName(note, semitones) {
        const currentIndex = this.getNoteIndex(note);
        if (currentIndex < 0) return note;
        const next = (currentIndex + semitones + 120) % 12;
        return this.notes[next];
    }

    dbToPercent(db) {
        const normalized = Math.max(-60, Math.min(0, db));
        return Math.round(((normalized + 60) / 60) * 100);
    }

    setStatus(message, resetMs = 0) {
        const status = document.getElementById('StatusText');
        if (!status) return;
        status.textContent = message;
        if (this.statusTimer) clearTimeout(this.statusTimer);
        if (resetMs > 0) {
            this.statusTimer = setTimeout(() => {
                status.textContent = this.isPlaying ? 'PLAYING' : 'READY';
                this.statusTimer = null;
            }, resetMs);
        }
    }

    getFavorites() {
        try {
            const stored = JSON.parse(window.flPreferenceStorage.getItem('ashe_generator_favorites_v1') || '[]');
            return Array.isArray(stored) ? stored : [];
        } catch (e) {
            return [];
        }
    }

    saveFavorites(favs) {
        try {
            window.flPreferenceStorage.setItem('ashe_generator_favorites_v1', JSON.stringify(favs));
        } catch (e) {
            /* local storage unavailable */
        }
    }

    syncVibeCards() {
        const cards = document.querySelectorAll('.vibe-card');
        cards.forEach(card => {
            const active = card.dataset.vibe === this.style;
            card.classList.toggle('active', active);
            card.setAttribute('aria-pressed', String(active));
        });
    }

    recomputeChordDegrees() {
        const keyIndex = this.getNoteIndex(this.key);
        const intervals = this.scales[this.mode] || this.scales.ionian;
        if (keyIndex < 0 || !Array.isArray(intervals)) return;

        this.progression = this.progression.map((chord) => {
            if (!chord?.root) return chord;
            const idx = this.getNoteIndex(chord.root);
            if (idx < 0) return chord;
            const rel = (idx - keyIndex + 12) % 12;
            const degreeIdx = intervals.indexOf(rel);
            return {
                ...chord,
                degree: degreeIdx >= 0 ? degreeIdx + 1 : (chord.degree || 1)
            };
        });
    }

    getLengthFromUI() {
        const slider = document.getElementById('LengthSlider');
        const raw = parseInt(slider?.value || `${this.progressionLength}`, 10);
        return Number.isFinite(raw) ? Math.max(4, Math.min(16, raw)) : this.progressionLength;
    }

    syncLengthUI() {
        const slider = document.getElementById('LengthSlider');
        const val = document.getElementById('LengthVal');
        if (slider) slider.value = `${this.progressionLength}`;
        if (val) val.textContent = `${this.progressionLength}`;
    }

    restartIfPlaying() {
        if (!this.isPlaying) return;
        this.stop();
        this.play();
    }

    syncAllUI() {
        const sync = (id, val) => { const el = document.getElementById(id); if (el) el.value = val; };
        sync('KeySelect', this.key);
        sync('ModeSelect', this.mode);
        sync('StyleSelect', this.style);
        sync('PadSoundSelect', this.padSound);
        sync('TempoSlider', this.bpm);
        sync('ComplexitySelect', this.harmonicIntent);
        sync('RhythmSelect', this.rhythmStyle);
        sync('SigUpper', this.timeSignature[0]);
        sync('SigLower', this.timeSignature[1]);
        sync('SwingSlider', Math.round(this.swingAmount * 100));
        const autoInvertEl = document.getElementById('AutoInvertToggle');
        if (autoInvertEl) autoInvertEl.checked = !!this.autoInvert;
        const voiceLeadEl = document.getElementById('VoiceLeadingToggle');
        if (voiceLeadEl) voiceLeadEl.checked = !!this.enableVoiceLeading;
        sync('VolPad', this.dbToPercent(this.levels.pad));
        sync('VolBass', this.dbToPercent(this.levels.bass));
        sync('VolDrums', this.dbToPercent(this.levels.drums));
        const tempoVal = document.getElementById('TempoVal');
        if (tempoVal) tempoVal.textContent = this.bpm;
        this.syncLengthUI();
        this.syncVibeCards();
        ['VolPad', 'VolBass', 'VolDrums'].forEach((id) => {
            const input = document.getElementById(id);
            if (input) this.updateFaderDisplay(input);
        });
    }

    // ========================================
    // SETTINGS PERSISTENCE
    // ========================================
    saveSettings() {
        try {
            const state = {
                key: this.key,
                mode: this.mode,
                style: this.style,
                padSound: this.padSound,
                bpm: this.bpm,
                progressionLength: this.progressionLength,
                rhythmStyle: this.rhythmStyle,
                harmonicIntent: this.harmonicIntent,
                timeSignature: [...this.timeSignature],
                swingAmount: this.swingAmount,
                enableVoiceLeading: this.enableVoiceLeading,
                autoInvert: this.autoInvert,
                levels: { ...this.levels },
                progression: JSON.parse(JSON.stringify(this.progression))
            };
            window.flPreferenceStorage.setItem(this.settingsKey, JSON.stringify(state));
        } catch (e) { /* storage full */ }
    }

    loadSettings() {
        try {
            const raw = window.flPreferenceStorage.getItem(this.settingsKey);
            if (!raw) return false;
            const state = JSON.parse(raw);
            this.key = state.key || this.key;
            this.mode = state.mode || this.mode;
            this.style = this.styleConfigs[state.style] ? state.style : this.style;
            this.padSound = state.padSound || this.padSound;
            this.bpm = Number.isFinite(state.bpm) ? state.bpm : this.bpm;
            this.progressionLength = Math.max(4, Math.min(16, state.progressionLength || this.progressionLength));
            this.rhythmStyle = state.rhythmStyle || this.rhythmStyle;
            this.harmonicIntent = state.harmonicIntent || this.harmonicIntent;
            if (Array.isArray(state.timeSignature) && state.timeSignature.length === 2) {
                this.timeSignature = state.timeSignature;
            }
            if (typeof state.swingAmount === 'number') this.swingAmount = state.swingAmount;
            this.enableVoiceLeading = state.enableVoiceLeading !== false;
            this.autoInvert = !!state.autoInvert;
            if (state.levels) this.levels = { ...this.levels, ...state.levels };
            const progression = this.sanitizeProgression(state.progression);
            if (progression.length > 0) {
                this.progression = progression;
            }
            return true;
        } catch (e) { return false; }
    }

    // ========================================
    // INITIALIZATION
    // ========================================
    init() {
        if (this.isInitialized) return;
        this.isInitialized = true;

        this.populateSelects();
        this.bindUI();

        // Restore settings, then fall back to seed/daily
        const restoredFromSettings = this.loadSettings();
        if (restoredFromSettings) {
            this.recomputeChordDegrees();
            this.syncAllUI();
        } else {
            this.syncAllUI();
        }

        const restoredFromSeed = this.parseSeed();
        if (!restoredFromSeed && !restoredFromSettings) {
            this.loadDailyProgression(false);
        }

        if (!Array.isArray(this.progression) || this.progression.length === 0) {
            this.generateProgression(this.progressionLength);
        }
        this.recomputeChordDegrees();
        this.renderChords();
        this.renderSavedTracks();
        this.loadFavorites();

        // Boot sequence closure
        const loader = document.getElementById('GeneratorLoader');
        if (loader) {
            loader.classList.add('is-loaded');
            setTimeout(() => loader.remove(), 500);
        }

        // Disable controls until CDN libs are ready
        this.waitForLibs();

        // ========== KEYBOARD SHORTCUTS ==========
        document.addEventListener('keydown', (e) => {
            if (e.target.closest('input, select, textarea, button, a, [contenteditable="true"], [role="button"]')) return;

            switch (e.code) {
                case 'Space':
                    e.preventDefault();
                    this.togglePlay();
                    break;
                case 'KeyR':
                    e.preventDefault();
                    this.rewind();
                    break;
                case 'KeyI':
                    e.preventDefault();
                    this.generateProgression();
                    break;
                case 'Escape':
                    // Close challenge banner if open
                    {
                        const banner = document.getElementById('ChallengeBanner');
                        if (banner && banner.style.display !== 'none') {
                            banner.style.display = 'none';
                        }
                    }
                    break;
                case 'Delete':
                case 'Backspace':
                    e.preventDefault();
                    if (this.progression.length > 1) {
                        this.progression.pop();
                        this.progressionLength = Math.max(4, Math.min(16, this.progression.length));
                        this.syncLengthUI();
                        this.recomputeChordDegrees();
                        this.renderChords();
                        this.restartIfPlaying();
                    }
                    break;
            }
        });

        // ========== STOP AUDIO ON PAGE HIDE ==========
        document.addEventListener('visibilitychange', () => {
            if (document.hidden && this.isPlaying) {
                this.stop();
            }
        });

        // ========== AUTO-SAVE SETTINGS ==========
        // Save whenever the user changes a meaningful setting
        this._scheduleSave = () => {
            clearTimeout(this._saveTimer);
            this._saveTimer = setTimeout(() => this.saveSettings(), 600);
        };
    }

    bindUI() {
        // Transport
        document.getElementById('BtnPlay')?.addEventListener('click', () => this.togglePlay());
        document.getElementById('BtnStop')?.addEventListener('click', () => this.stop());
        document.getElementById('BtnRewind')?.addEventListener('click', () => this.rewind());
        document.getElementById('BtnTapTempo')?.addEventListener('click', () => this.tapTempo());

        // Actions
        document.getElementById('BtnInspire')?.addEventListener('click', () => this.inspire());
        document.getElementById('BtnDaily')?.addEventListener('click', () => this.loadDailyProgression(true));
        document.getElementById('BtnRandomProg')?.addEventListener('click', () => this.shuffleProgression());
        document.getElementById('BtnAddChord')?.addEventListener('click', () => this.addChord());
        document.getElementById('BtnTransposeDown')?.addEventListener('click', () => this.transposeProgression(-1));
        document.getElementById('BtnTransposeUp')?.addEventListener('click', () => this.transposeProgression(1));
        document.getElementById('BtnCopyChords')?.addEventListener('click', () => this.copyChordsToClipboard());
        document.getElementById('BtnExport')?.addEventListener('click', () => this.exportMIDI());
        document.getElementById('BtnShare')?.addEventListener('click', () => this.shareLink());
        document.getElementById('BtnSaveFavorite')?.addEventListener('click', () => this.toggleFavorite());
        document.getElementById('BtnStudioChallenge')?.addEventListener('click', () => this.getStudioChallenge());

        // Pad Sound
        document.getElementById('PadSoundSelect')?.addEventListener('change', (e) => {
            this.padSound = e.target.value;
            if (this.audioReady) this.rebuildPadSynth();
            this._scheduleSave?.();
        });

        // Hash Save/Recall
        document.getElementById('BtnSaveTrack')?.addEventListener('click', () => {
            const nameInput = document.getElementById('TrackNameInput');
            const name = nameInput?.value?.trim() || null;
            const hash = this.saveTrack(name);
            if (!hash) return;
            if (nameInput) nameInput.value = '';
            this.renderSavedTracks();
            this.setStatus(`Saved! Hash: ${hash}`, 3000);
        });

        document.getElementById('HashRecallInput')?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                const hash = e.target.value.trim();
                if (this.loadTrack(hash)) {
                    this.setStatus(`Loaded: ${hash}`, 3000);
                    e.target.value = '';
                } else {
                    this.setStatus(`Hash not found: ${hash}`, 3000);
                }
            }
        });

        // Vibes
        const vibeCards = document.querySelectorAll('.vibe-card');
        vibeCards.forEach(card => {
            card.addEventListener('click', () => {
                this.applyVibePreset(card.dataset.vibe);
            });
        });

        // Advanced Toggle
        document.getElementById('ToggleAdvanced')?.addEventListener('click', () => {
            const drawer = document.getElementById('AdvancedSettings');
            const btn = document.getElementById('ToggleAdvanced');
            if (drawer) {
                const isHidden = drawer.hasAttribute('hidden');
                drawer.toggleAttribute('hidden', !isHidden);
                if (btn) btn.setAttribute('aria-expanded', String(isHidden));
            }
        });

        // Settings
        document.getElementById('TempoSlider')?.addEventListener('input', (e) => {
            this.bpm = parseInt(e.target.value);
            const tempoEl = document.getElementById('TempoVal');
            if (tempoEl) tempoEl.textContent = this.bpm;
            if ((typeof Tone !== 'undefined' && Tone.Transport)) Tone.Transport.bpm.value = this.bpm;
            this._scheduleSave?.();
        });

        document.getElementById('KeySelect')?.addEventListener('change', (e) => {
            this.key = e.target.value;
            this.generateProgression(this.progressionLength);
            this._scheduleSave?.();
        });

        document.getElementById('ModeSelect')?.addEventListener('change', (e) => {
            this.mode = e.target.value;
            this.generateProgression(this.progressionLength);
            this._scheduleSave?.();
        });

        document.getElementById('RhythmSelect')?.addEventListener('change', (e) => {
            this.rhythmStyle = e.target.value;
            this._scheduleSave?.();
        });

        document.getElementById('StyleSelect')?.addEventListener('change', (e) => {
            this.style = e.target.value;
            this.syncVibeCards();
            this.applyStyle();
            this._scheduleSave?.();
        });

        document.getElementById('ComplexitySelect')?.addEventListener('change', (e) => {
            this.harmonicIntent = e.target.value;
            this.generateProgression(this.progressionLength);
            this._scheduleSave?.();
        });

        document.getElementById('VoiceLeadingToggle')?.addEventListener('change', (e) => {
            this.enableVoiceLeading = e.target.checked;
            this._scheduleSave?.();
        });

        document.getElementById('AutoInvertToggle')?.addEventListener('change', (e) => {
            this.autoInvert = e.target.checked;
            this.generateProgression(this.progressionLength);
            this._scheduleSave?.();
        });

        document.getElementById('LengthSlider')?.addEventListener('input', (e) => {
            this.progressionLength = Math.max(4, Math.min(16, parseInt(e.target.value, 10) || 8));
            this.syncLengthUI();
        });

        document.getElementById('LengthSlider')?.addEventListener('change', () => {
            this.generateProgression(this.progressionLength);
            this._scheduleSave?.();
        });

        document.getElementById('BtnMetronome')?.addEventListener('click', () => this.toggleMetronome());

        document.getElementById('SigUpper')?.addEventListener('change', (e) => {
            this.timeSignature[0] = parseInt(e.target.value);
            this.updateTimeSignature();
            this._scheduleSave?.();
        });

        document.getElementById('SigLower')?.addEventListener('change', (e) => {
            this.timeSignature[1] = parseInt(e.target.value);
            this.updateTimeSignature();
            this._scheduleSave?.();
        });

        document.getElementById('SwingSlider')?.addEventListener('input', (e) => {
            this.swingAmount = parseInt(e.target.value) / 100;
            if ((typeof Tone !== 'undefined' && Tone.Transport)) {
                Tone.Transport.swing = this.swingAmount;
                Tone.Transport.swingSubdivision = this.getSwingSubdivision();
            }
            this._scheduleSave?.();
        });

        document.getElementById('ProgLibrary')?.addEventListener('change', (e) => {
            this.loadFromLibrary(e.target.value);
        });

        // Faders
        ['VolPad', 'VolBass', 'VolDrums'].forEach(id => {
            document.getElementById(id)?.addEventListener('input', (e) => {
                const channel = id.replace('Vol', '').toLowerCase();
                this.levels[channel] = this.percentToDb(parseInt(e.target.value));
                this.updateMix();
                this.updateFaderDisplay(e.target);
                this._scheduleSave?.();
            });
        });
    }

    populateSelects() {
        const keySelect = document.getElementById('KeySelect');
        if (keySelect && keySelect.children.length === 0) {
            this.notes.forEach(n => {
                const opt = document.createElement('option');
                opt.value = n;
                opt.textContent = n;
                keySelect.appendChild(opt);
            });
        }
    }

    // ========================================
    // CDN LOADING GUARD
    // ========================================
    setLibsReady(audioAvailable, midiAvailable) {
        const audioControls = document.querySelectorAll('#BtnPlay, #BtnStop, #BtnRewind, #BtnMetronome');
        audioControls.forEach(el => {
            el.disabled = !audioAvailable;
            el.setAttribute('aria-disabled', audioAvailable ? 'false' : 'true');
        });

        const tapTempo = document.getElementById('BtnTapTempo');
        if (tapTempo) {
            tapTempo.disabled = false;
            tapTempo.setAttribute('aria-disabled', 'false');
        }

        const exportButton = document.getElementById('BtnExport');
        const exportAvailable = audioAvailable && midiAvailable;
        if (exportButton) {
            exportButton.disabled = !exportAvailable;
            exportButton.setAttribute('aria-disabled', exportAvailable ? 'false' : 'true');
            exportButton.title = exportAvailable ? 'Export MIDI' : 'MIDI export library unavailable';
        }
    }

    waitForLibs() {
        const MAX_WAIT_MS = 12000;
        const started = Date.now();
        const check = () => {
            const audioAvailable = typeof Tone !== 'undefined';
            const midiAvailable = typeof MidiWriter !== 'undefined';
            this.setLibsReady(audioAvailable, midiAvailable);

            if (audioAvailable && midiAvailable) {
                this.setStatus('READY');
                return;
            }
            if (Date.now() - started >= MAX_WAIT_MS) {
                this.setStatus(audioAvailable
                    ? 'Audio ready · MIDI export unavailable'
                    : 'Audio engine unavailable — check your connection');
                return;
            }
            setTimeout(check, 200);
        };
        this.setLibsReady(false, false);
        this.setStatus('LOADING AUDIO LIBS...');
        check();
    }

    // ========================================
    // AUDIO ENGINE
    // ========================================
    async initAudio() {
        if (this.audioReady) return;

        await Tone.start();
        /* Audio context started */

        // Master chain: dry bus + FX sends -> compressor -> limiter -> EQ -> destination
        this.fx.masterEQ = new Tone.EQ3({ low: 0, mid: 0, high: 1 }).toDestination();
        this.fx.limiter = new Tone.Limiter(-1).connect(this.fx.masterEQ);
        this.fx.compressor = new Tone.Compressor({ threshold: -18, ratio: 3, attack: 0.005, release: 0.2 }).connect(this.fx.limiter);
        this.fx.mixBus = new Tone.Gain(1).connect(this.fx.compressor);

        // Time-based effects live on send paths (fully wet)
        this.fx.reverb = new Tone.Reverb({ decay: 4.2, preDelay: 0.02, wet: 1 });
        this.fx.delay = new Tone.PingPongDelay({ delayTime: '8n', feedback: 0.24, wet: 1 }).connect(this.fx.reverb);
        this.fx.reverb.connect(this.fx.mixBus);
        try {
            await this.fx.reverb.generate();
        } catch (e) {
            /* impulse generation fallback */
        }
        this.fx.padSend = new Tone.Gain(0.2).connect(this.fx.delay);
        this.fx.bassSend = new Tone.Gain(0.08).connect(this.fx.reverb);
        this.fx.drumSend = new Tone.Gain(0.06).connect(this.fx.reverb);

        // Tone-shaping processors
        this.fx.padDrive = new Tone.Distortion({ distortion: 0.05, wet: 0.3 });
        this.fx.padChorus = new Tone.Chorus({ frequency: 1.2, delayTime: 2.5, depth: 0.2, wet: 0.25 }).start();
        this.fx.bassFilter = new Tone.Filter({ type: 'lowpass', frequency: 1000, rolloff: -24 });
        this.fx.bassDrive = new Tone.Distortion({ distortion: 0.1, wet: 0.25 });
        this.fx.drumTone = new Tone.Filter({ type: 'lowpass', frequency: 11000, rolloff: -12 });
        this.fx.drumCompressor = new Tone.Compressor({ threshold: -16, ratio: 4, attack: 0.003, release: 0.08 });

        // Analyzers for VU meters
        this.fx.analyzerPad = new Tone.Analyser('waveform', 256);
        this.fx.analyzerBass = new Tone.Analyser('waveform', 256);
        this.fx.analyzerDrums = new Tone.Analyser('waveform', 256);

        // Create synths based on current style
        this.createSynths();
        this.createDrumKit();

        this.audioReady = true;
        this.applyStyle();
        this.updateMix();
    }

    applyVibePreset(vibeId) {
        if (!this.styleConfigs[vibeId]) return;
        this.style = vibeId;
        const styleSelect = document.getElementById('StyleSelect');
        if (styleSelect) styleSelect.value = vibeId;
        this.syncVibeCards();

        // Vibe-specific refined settings
        const presets = {
            prog_rock: { complexity: 'sophisticated', rhythm: 'strum' },
            ashe_fusion: { complexity: 'pro', rhythm: 'arpeggio' },
            tech_metal: { complexity: 'sophisticated', rhythm: 'syncopated' },
            clean_fusion: { complexity: 'sophisticated', rhythm: 'jazz_comp' },
            atmospheric: { complexity: 'stable', rhythm: 'swell' },
            cinematic: { complexity: 'pro', rhythm: 'swell' },
            neo_soul: { complexity: 'sophisticated', rhythm: 'jazz_comp' },
            djent: { complexity: 'stable', rhythm: 'syncopated' },
            jazz_swing: { complexity: 'pro', rhythm: 'jazz_comp' }
        };

        const config = presets[vibeId];
        if (config) {
            this.harmonicIntent = config.complexity;
            this.rhythmStyle = config.rhythm;

            // Sync UI selects if they exist (for advanced mode users)
            if (document.getElementById('ComplexitySelect')) document.getElementById('ComplexitySelect').value = config.complexity;
            if (document.getElementById('RhythmSelect')) document.getElementById('RhythmSelect').value = config.rhythm;
        }

        this.applyStyle();

        // Auto-regenerate progression if not playing to reflect vibe changes
        if (!this.isPlaying) {
            this.generateProgression(this.progressionLength);
        }

        this._scheduleSave?.();
    }

    createSynths() {
        const config = this.styleConfigs[this.style] || this.styleConfigs.prog_rock;
        const padConfig = this.padSoundConfigs[this.padSound] || this.padSoundConfigs.warm_pad;
        const padEnvelope = {
            attack: Math.max(0.001, (padConfig.envelope.attack * 0.55) + (config.padAttack * 0.45)),
            decay: padConfig.envelope.decay,
            sustain: padConfig.envelope.sustain,
            release: Math.max(0.08, (padConfig.envelope.release * 0.55) + (config.padRelease * 0.45))
        };

        // PAD SYNTH (uses padSound config)
        this.synths.pad = new Tone.PolySynth(Tone.Synth, {
            oscillator: padConfig.oscillator,
            envelope: padEnvelope
        });

        // Add pad filter
        this.fx.padFilter = new Tone.Filter({
            type: padConfig.filter?.type || 'lowpass',
            frequency: padConfig.filter?.frequency || 4000,
            rolloff: padConfig.filter?.rolloff || -12
        });
        this.synths.pad.connect(this.fx.padFilter);
        this.fx.padFilter.connect(this.fx.padDrive);
        this.fx.padDrive.connect(this.fx.padChorus);
        this.fx.padChorus.connect(this.fx.analyzerPad);
        this.fx.analyzerPad.connect(this.fx.mixBus);
        this.fx.analyzerPad.connect(this.fx.padSend);

        // BASS SYNTH
        this.synths.bass = new Tone.MonoSynth({
            oscillator: { type: config.bassType },
            envelope: { attack: 0.05, decay: 0.3, sustain: 0.4, release: 0.8 },
            filterEnvelope: { attack: 0.01, decay: 0.1, baseFrequency: 150, octaves: 2.5 }
        });
        this.synths.bass.connect(this.fx.bassFilter);
        this.fx.bassFilter.connect(this.fx.bassDrive);
        this.fx.bassDrive.connect(this.fx.analyzerBass);
        this.fx.analyzerBass.connect(this.fx.mixBus);
        this.fx.analyzerBass.connect(this.fx.bassSend);

        // METRONOME
        this.synths.metronome = new Tone.MembraneSynth({
            pitchDecay: 0.01,
            octaves: 2,
            oscillator: { type: 'sine' },
            envelope: { attack: 0.001, decay: 0.05, sustain: 0, release: 0.05 }
        }).connect(this.fx.mixBus);
        this.synths.metronome.volume.value = -12;
    }

    rebuildPadSynth() {
        if (!this.audioReady) return;
        const padConfig = this.padSoundConfigs[this.padSound] || this.padSoundConfigs.warm_pad;
        const config = this.styleConfigs[this.style] || this.styleConfigs.prog_rock;
        const padEnvelope = {
            attack: Math.max(0.001, (padConfig.envelope.attack * 0.55) + (config.padAttack * 0.45)),
            decay: padConfig.envelope.decay,
            sustain: padConfig.envelope.sustain,
            release: Math.max(0.08, (padConfig.envelope.release * 0.55) + (config.padRelease * 0.45))
        };

        // Rebuild pad with new sound
        if (this.synths.pad) {
            this.synths.pad.releaseAll();
            this.synths.pad.disconnect();
            this.synths.pad.dispose();
        }
        if (this.fx.padFilter) {
            this.fx.padFilter.disconnect();
            this.fx.padFilter.dispose();
        }

        this.synths.pad = new Tone.PolySynth(Tone.Synth, {
            oscillator: padConfig.oscillator,
            envelope: padEnvelope
        });

        this.fx.padFilter = new Tone.Filter({
            type: padConfig.filter?.type || 'lowpass',
            frequency: padConfig.filter?.frequency || 4000,
            rolloff: padConfig.filter?.rolloff || -12
        });

        this.fx.padDrive.disconnect();
        this.fx.padChorus.disconnect();
        this.synths.pad.connect(this.fx.padFilter);
        this.fx.padFilter.connect(this.fx.padDrive);
        this.fx.padDrive.connect(this.fx.padChorus);
        this.fx.padChorus.connect(this.fx.analyzerPad);
        this.updateMix();
        this.applyStyle();
    }

    createDrumKit() {
        // Kick
        this.drumKit.kick = new Tone.MembraneSynth({
            pitchDecay: 0.05,
            octaves: 6,
            oscillator: { type: 'sine' },
            envelope: { attack: 0.001, decay: 0.4, sustain: 0, release: 0.4 }
        });

        // Snare
        this.drumKit.snare = new Tone.NoiseSynth({
            noise: { type: 'white' },
            envelope: { attack: 0.005, decay: 0.15, sustain: 0, release: 0.1 }
        });

        // Hi-hat
        this.drumKit.hat = new Tone.MetalSynth({
            frequency: 200,
            envelope: { attack: 0.001, decay: 0.1, release: 0.01 },
            harmonicity: 5.1,
            modulationIndex: 32,
            resonance: 4000,
            octaves: 1.5
        });

        // Crash
        this.drumKit.crash = new Tone.MetalSynth({
            frequency: 300,
            envelope: { attack: 0.001, decay: 1.5, release: 0.3 },
            harmonicity: 5.1,
            modulationIndex: 40,
            resonance: 5000,
            octaves: 1.5
        });

        // Connect all drums
        const drumMerge = new Tone.Gain();
        drumMerge.connect(this.fx.drumTone);
        this.fx.drumTone.connect(this.fx.drumCompressor);
        this.fx.drumCompressor.connect(this.fx.analyzerDrums);
        this.fx.analyzerDrums.connect(this.fx.mixBus);
        this.fx.analyzerDrums.connect(this.fx.drumSend);

        Object.values(this.drumKit).forEach(drum => drum.connect(drumMerge));
    }

    applyStyle() {
        if (!this.audioReady) return;
        const config = this.styleConfigs[this.style] || this.styleConfigs.prog_rock;

        // FX send character
        this.fx.reverb.decay = 2.6 + ((config.reverb || 0.3) * 4.5);
        this.fx.padSend.gain.rampTo(config.delaySend ?? 0.2, 0.15);
        this.fx.bassSend.gain.rampTo((config.reverbSend ?? 0.15) * 0.45, 0.15);
        this.fx.drumSend.gain.rampTo((config.reverbSend ?? 0.15) * 0.35, 0.15);
        this.fx.delay.feedback.rampTo(config.delayFeedback ?? 0.24, 0.15);
        this.fx.delay.delayTime.value = config.delayTime || '8n';

        // Tone shaping
        this.fx.padFilter.frequency.rampTo(config.padFilterHz ?? 2200, 0.12);
        this.fx.padDrive.distortion = config.padDrive ?? 0.05;
        this.fx.padDrive.wet.rampTo(Math.min(0.85, 0.2 + ((config.padDrive ?? 0.05) * 2.4)), 0.12);
        this.fx.padChorus.wet.rampTo(config.padChorus ?? 0.25, 0.12);
        this.fx.bassFilter.frequency.rampTo(config.bassFilterHz ?? 1100, 0.1);
        this.fx.bassDrive.distortion = config.bassDrive ?? 0.1;
        this.fx.bassDrive.wet.rampTo(Math.min(0.9, 0.18 + ((config.bassDrive ?? 0.1) * 1.8)), 0.1);
        this.fx.drumTone.frequency.rampTo(config.drumToneHz ?? 11000, 0.1);
        this.fx.masterEQ.low.value = config.masterLow ?? 0;
        this.fx.masterEQ.high.value = config.masterHigh ?? 1;

        // Drum voicing per style pattern
        const drumVoicing = {
            rock: { kickDecay: 0.38, kickOct: 6, snareDecay: 0.15, hatDecay: 0.095, crashDecay: 1.4, hatRes: 3900 },
            tech: { kickDecay: 0.24, kickOct: 7, snareDecay: 0.11, hatDecay: 0.05, crashDecay: 1.2, hatRes: 5200 },
            fusion: { kickDecay: 0.34, kickOct: 5.5, snareDecay: 0.13, hatDecay: 0.1, crashDecay: 1.5, hatRes: 4200 },
            jazz: { kickDecay: 0.32, kickOct: 4.5, snareDecay: 0.12, hatDecay: 0.14, crashDecay: 1.8, hatRes: 3200 },
            ambient: { kickDecay: 0.52, kickOct: 4.2, snareDecay: 0.2, hatDecay: 0.2, crashDecay: 2.4, hatRes: 2600 },
            djent: { kickDecay: 0.22, kickOct: 7.5, snareDecay: 0.1, hatDecay: 0.045, crashDecay: 1.15, hatRes: 5600 },
            epic: { kickDecay: 0.58, kickOct: 5.8, snareDecay: 0.18, hatDecay: 0.16, crashDecay: 2.1, hatRes: 3000 }
        };
        const voice = drumVoicing[config.drumPattern] || drumVoicing.rock;
        this.drumKit.kick?.set({
            octaves: voice.kickOct,
            envelope: { decay: voice.kickDecay, release: Math.max(0.24, voice.kickDecay + 0.04) }
        });
        this.drumKit.snare?.set({
            envelope: { decay: voice.snareDecay, release: Math.max(0.08, voice.snareDecay * 0.75) }
        });
        this.drumKit.hat?.set({
            resonance: voice.hatRes,
            envelope: { decay: voice.hatDecay, release: Math.max(0.01, voice.hatDecay * 0.14) }
        });
        this.drumKit.crash?.set({
            envelope: { decay: voice.crashDecay, release: Math.max(0.2, voice.crashDecay * 0.22) }
        });

        if (config.swing !== undefined) {
            this.swingAmount = config.swing;
            Tone.Transport.swing = this.swingAmount;
            Tone.Transport.swingSubdivision = this.getSwingSubdivision();
            const slider = document.getElementById('SwingSlider');
            if (slider) slider.value = this.swingAmount * 100;
        }

        this.updateMix();
    }

    updateMix() {
        if (!this.audioReady) return;
        const style = this.styleConfigs[this.style] || this.styleConfigs.prog_rock;
        const toLinear = (db) => Math.pow(10, db / 20);
        const padLin = Math.max(0.08, toLinear(this.levels.pad));
        const bassLin = Math.max(0.08, toLinear(this.levels.bass));
        const drumLin = Math.max(0.08, toLinear(this.levels.drums));

        this.synths.pad?.volume?.rampTo(this.levels.pad, 0.1);
        this.synths.bass?.volume?.rampTo(this.levels.bass, 0.1);
        if (this.drumKit.kick?.volume) this.drumKit.kick.volume.value = this.levels.drums + 1.5;
        if (this.drumKit.snare?.volume) this.drumKit.snare.volume.value = this.levels.drums - 1;
        if (this.drumKit.hat?.volume) this.drumKit.hat.volume.value = this.levels.drums - 7;
        if (this.drumKit.crash?.volume) this.drumKit.crash.volume.value = this.levels.drums - 4;

        this.fx.padSend.gain.rampTo((style.delaySend ?? 0.2) * padLin, 0.1);
        this.fx.bassSend.gain.rampTo((style.reverbSend ?? 0.15) * 0.45 * bassLin, 0.1);
        this.fx.drumSend.gain.rampTo((style.reverbSend ?? 0.15) * 0.35 * drumLin, 0.1);
    }

    percentToDb(pct) {
        if (pct <= 0) return -60;
        return (pct / 100) * 60 - 60;
    }

    updateFaderDisplay(fader) {
        const valueId = `${fader.id}Value`;
        const label = document.getElementById(valueId) || fader.closest('.mixer-strip')?.querySelector('.fader-value');
        const channel = fader.id.replace('Vol', '').toLowerCase();
        if (label) label.textContent = `${Math.round(this.levels[channel])} dB`;
    }

    getTimingGrid() {
        const beats = Math.max(1, parseInt(this.timeSignature?.[0], 10) || 4);
        const unit = Math.max(1, parseInt(this.timeSignature?.[1], 10) || 4);
        const beat = Tone.Time(`${unit}n`).toSeconds();
        return {
            beats,
            unit,
            beat,
            half: beat / 2,
            quarter: beat / 4,
            bar: beat * beats
        };
    }

    getSwingSubdivision() {
        const unit = Math.max(1, parseInt(this.timeSignature?.[1], 10) || 4);
        return `${Math.max(8, unit * 2)}n`;
    }

    isFourFourGrid(grid = this.getTimingGrid()) {
        return grid.beats === 4 && grid.unit === 4;
    }

    humanizeTime(time, spread = 0.004) {
        const style = this.styleConfigs[this.style] || this.styleConfigs.prog_rock;
        const scaledSpread = spread * (style.timeHumanize ?? 1);
        return Math.max(0, time + ((Math.random() * 2 - 1) * scaledSpread));
    }

    humanizeVelocity(base = 0.8, spread = 0.08) {
        const style = this.styleConfigs[this.style] || this.styleConfigs.prog_rock;
        const scaledSpread = spread * (style.velocityHumanize ?? 1);
        return Math.max(0.02, Math.min(1, base + ((Math.random() * 2 - 1) * scaledSpread)));
    }

    // ========================================
    // PLAYBACK
    // ========================================
    togglePlay() {
        if (this.isPlaying) {
            this.stop();
        } else {
            this.play();
        }
    }

    async play() {
        if (this.isPlaying) this.stop();
        if (typeof Tone === 'undefined') {
            this.setStatus('Audio engine unavailable — check your connection');
            return;
        }
        await this.initAudio();

        this.isPlaying = true;
        this.currentBar = 0;
        this.barsPlayCount = 0;
        Tone.Transport.bpm.value = this.bpm;
        Tone.Transport.timeSignature = this.timeSignature;
        Tone.Transport.swing = this.swingAmount;
        Tone.Transport.swingSubdivision = this.getSwingSubdivision();

        // Calculate total bars
        let totalBars = 0;
        this.progression.forEach(c => totalBars += c.bars);

        // Schedule playback
        let barOffset = 0;
        this.progression.forEach((chord, chordIndex) => {
            for (let b = 0; b < chord.bars; b++) {
                const barTime = `${barOffset + b}m`;
                Tone.Transport.schedule((time) => {
                    this.playBar(chord, time, b === 0, b, chordIndex);
                    Tone.Draw.schedule(() => this.updatePlayhead(chordIndex, b), time);
                }, barTime);
            }
            barOffset += chord.bars;
        });

        // Loop point
        Tone.Transport.loopEnd = `${totalBars}m`;
        Tone.Transport.loop = true;
        Tone.Transport.start();

        // UI
        this.updateTransportUI(true);
        this.vuAnimationId = requestAnimationFrame(() => this.animateVU());
    }

    toggleMetronome() {
        this.metronomeActive = !this.metronomeActive;
        const btn = document.getElementById('BtnMetronome');
        if (btn) {
            btn.classList.toggle('active', this.metronomeActive);
            btn.setAttribute('aria-pressed', String(this.metronomeActive));
        }

        // Schedule metronome separate from bar loop if playing
        if (this.isPlaying) {
            this.stop();
            this.play();
        }
    }

    updateTimeSignature() {
        if ((typeof Tone !== 'undefined' && Tone.Transport)) {
            Tone.Transport.timeSignature = this.timeSignature;
            Tone.Transport.swingSubdivision = this.getSwingSubdivision();
        }
        this.renderChords();
    }

    loadFromLibrary(id) {
        const prog = this.library[id];
        if (prog) {
            this.progression = JSON.parse(JSON.stringify(prog));
            this.progressionLength = Math.max(4, Math.min(16, this.progression.length));
            this.syncLengthUI();
            this.recomputeChordDegrees();
            this.renderChords();
            this.setStatus(`Loaded: ${id.replace(/_/g, ' ')}`, 1800);
            this.restartIfPlaying();
            this._scheduleSave?.();
        }
    }

    stop() {
        if (typeof Tone !== 'undefined' && Tone.Transport) {
            Tone.Transport.stop();
            Tone.Transport.cancel();
        }
        // Release any held synth notes to avoid stuck oscillators
        if (this.audioReady) {
            try { this.synths.pad?.releaseAll(); } catch (e) { /* ignore */ }
        }
        this.isPlaying = false;
        this.updateTransportUI(false);
        this.stopVUAnimation();
        this.clearPlayhead();
    }

    rewind() {
        if (this.isPlaying) {
            this.stop();
            setTimeout(() => this.play(), 100);
        }
    }

    playBar(chord, time, isFirst, barIndex, chordIndex) {
        this.barsPlayCount++;
        const style = this.styleConfigs[this.style] || this.styleConfigs.prog_rock;

        // 1. PAD - Style-specific rhythm styles
        const notes = this.getChordNotes(chord.root, chord.quality, chord.extensions || [], chord.inversion || 0);
        this.playChordStyle(notes, time, style);

        // 2. BASS - Style-specific patterns
        this.playBassPattern(chord, time, style);

        // 3. DRUMS - Style-specific patterns
        this.playDrumPattern(time, style, isFirst);

        // 4. METRONOME
        if (this.metronomeActive) {
            const grid = this.getTimingGrid();
            for (let i = 0; i < grid.beats; i++) {
                const beatTime = time + (i * grid.beat);
                const freq = i === 0 ? 'C5' : 'C4';
                this.synths.metronome.triggerAttackRelease(freq, '32n', beatTime);
            }
        }
    }

    playChordStyle(notes, time, style) {
        const intensity = (this.barsPlayCount % 32) / 32;
        const velocity = 0.4 + (intensity * 0.4);
        const grid = this.getTimingGrid();

        if (!this.isFourFourGrid(grid)) {
            const beat = grid.beat;
            switch (this.rhythmStyle) {
                case 'strum':
                    notes.forEach((note, i) => {
                        const t = this.humanizeTime(time + (i * 0.018), 0.003);
                        this.synths.pad.triggerAttackRelease(note, grid.bar * 0.95, t, this.humanizeVelocity(velocity, 0.06));
                    });
                    break;
                case 'arpeggio':
                    for (let i = 0; i < grid.beats; i++) {
                        const note = notes[i % notes.length];
                        const t = this.humanizeTime(time + (i * beat), 0.0035);
                        this.synths.pad.triggerAttackRelease(note, beat * 0.92, t, this.humanizeVelocity(velocity - 0.08, 0.05));
                    }
                    break;
                case 'syncopated':
                case 'jazz_comp':
                    this.synths.pad.triggerAttackRelease(notes, beat * 0.9, this.humanizeTime(time, 0.003), this.humanizeVelocity(velocity, 0.05));
                    if (grid.beats > 2) {
                        this.synths.pad.triggerAttackRelease(
                            notes,
                            beat * 0.82,
                            this.humanizeTime(time + (Math.floor(grid.beats / 2) * beat), 0.004),
                            this.humanizeVelocity(velocity * 0.78, 0.05)
                        );
                    }
                    break;
                case 'swell':
                    this.synths.pad.set({ envelope: { attack: Math.max(0.8, style.padAttack) } });
                    this.synths.pad.triggerAttackRelease(notes, grid.bar * 0.95, this.humanizeTime(time, 0.002), this.humanizeVelocity(velocity, 0.03));
                    setTimeout(() => this.synths.pad.set({ envelope: { attack: style.padAttack } }), 2000);
                    break;
                default:
                    this.synths.pad.triggerAttackRelease(notes, grid.bar * 0.95, this.humanizeTime(time, 0.003), this.humanizeVelocity(velocity, 0.05));
                    break;
            }
            return;
        }

        switch (this.rhythmStyle) {
            case 'strum':
                // Rapid successions like a strum
                notes.forEach((note, i) => {
                    const t = this.humanizeTime(time + (i * 0.02), 0.003);
                    this.synths.pad.triggerAttackRelease(note, '1n', t, this.humanizeVelocity(velocity, 0.06));
                });
                break;
            case 'arpeggio':
                // Flowing arpeggios
                notes.forEach((note, i) => {
                    const t = this.humanizeTime(time + (i * Tone.Time('8n').toSeconds()), 0.004);
                    this.synths.pad.triggerAttackRelease(note, '4n', t, this.humanizeVelocity(velocity - 0.1, 0.05));
                });
                break;
            case 'swell':
                // Slow attack swell
                this.synths.pad.set({ envelope: { attack: 1.5 } });
                this.synths.pad.triggerAttackRelease(notes, '1n', this.humanizeTime(time, 0.002), this.humanizeVelocity(velocity, 0.03));
                setTimeout(() => this.synths.pad.set({ envelope: { attack: style.padAttack } }), 2000);
                break;
            case 'syncopated':
                // Pop/Funk syncopation
                this.synths.pad.triggerAttackRelease(notes, '8n', this.humanizeTime(time, 0.003), this.humanizeVelocity(velocity, 0.05));
                this.synths.pad.triggerAttackRelease(
                    notes,
                    '8n',
                    this.humanizeTime(time + Tone.Time('4n.').toSeconds(), 0.003),
                    this.humanizeVelocity(velocity * 0.8, 0.05)
                );
                break;
            case 'jazz_comp':
                // Syncopated jazz comping
                if (Math.random() > 0.3) {
                    this.synths.pad.triggerAttackRelease(
                        notes,
                        '8n',
                        this.humanizeTime(time + Tone.Time('8n').toSeconds(), 0.005),
                        this.humanizeVelocity(velocity, 0.06)
                    );
                }
                if (Math.random() > 0.5) {
                    this.synths.pad.triggerAttackRelease(
                        notes,
                        '8n',
                        this.humanizeTime(time + Tone.Time('2n.').toSeconds(), 0.005),
                        this.humanizeVelocity(velocity * 0.7, 0.06)
                    );
                }
                break;
            default:
                this.synths.pad.triggerAttackRelease(notes, '1n', this.humanizeTime(time, 0.003), this.humanizeVelocity(velocity, 0.05));
        }
    }

    playBassPattern(chord, time, style) {
        // Handle Slash Chords / Inversions in bass
        let bassRoot = chord.root;
        if (chord.inversion > 0) {
            const chordNotes = this.getChordNotes(chord.root, chord.quality, chord.extensions || [], chord.inversion);
            bassRoot = chordNotes[0].replace(/[0-9]/g, '');
        }

        const rootIdx = this.getNoteIndex(bassRoot);
        const normalizedRoot = rootIdx < 0 ? 0 : rootIdx;
        const bassNote = `${this.notes[normalizedRoot]}2`;
        const grid = this.getTimingGrid();
        const pattern = style.drumPattern;
        const qn = Tone.Time('4n').toSeconds();
        const en = Tone.Time('8n').toSeconds();

        // Helper: get a chromatic approach note
        const approachNote = (targetIdx, half) => {
            const idx = (targetIdx + (half ? -1 : 1) + 12) % 12;
            return `${this.notes[idx]}2`;
        };

        const bassHit = (note, duration, hitTime, velocity = 0.8) => {
            this.synths.bass.triggerAttackRelease(
                note,
                duration,
                this.humanizeTime(hitTime, 0.003),
                this.humanizeVelocity(velocity, 0.06)
            );
        };

        if (!this.isFourFourGrid(grid)) {
            this.playBassPatternOddTime({
                time,
                style,
                grid,
                bassNote,
                normalizedRoot,
                bassHit,
                approachNote
            });
            return;
        }

        switch (pattern) {
            case 'rock':
                bassHit(bassNote, '8n', time, 0.82);
                bassHit(bassNote, '8n', time + qn, 0.78);
                bassHit(bassNote, '8n', time + qn * 2, 0.84);
                bassHit(bassNote, '8n', time + qn * 3, 0.79);
                break;
            case 'jazz': {
                // TRUE WALKING BASS: Root → 3rd → 5th → approach
                const third = `${this.notes[(normalizedRoot + 4) % 12]}2`;
                const fifth = `${this.notes[(normalizedRoot + 7) % 12]}2`;
                const approach = approachNote(normalizedRoot, Math.random() > 0.5);
                bassHit(bassNote, '4n', time, 0.78);
                bassHit(third, '4n', time + qn, 0.74);
                bassHit(fifth, '4n', time + qn * 2, 0.75);
                bassHit(approach, '4n', time + qn * 3, 0.7);
                break;
            }
            case 'fusion': {
                // Groovy walking with ghost notes
                const fifth = `${this.notes[(normalizedRoot + 7) % 12]}2`;
                bassHit(bassNote, '8n', time, 0.9);
                if (Math.random() > 0.3) bassHit(bassNote, '16n', time + en, 0.34);
                bassHit(fifth, '8n', time + qn, 0.7);
                bassHit(bassNote, '8n', time + qn * 2, 0.8);
                if (Math.random() > 0.4) {
                    const chromatic = approachNote(normalizedRoot, true);
                    bassHit(chromatic, '8n', time + qn * 3, 0.62);
                }
                break;
            }
            case 'ambient':
                bassHit(bassNote, '1n', time, 0.6);
                break;
            case 'djent':
                bassHit(bassNote, '16n', time, 1.0);
                bassHit(bassNote, '16n', time + en * 1.5, 0.8);
                bassHit(bassNote, '16n', time + qn, 0.9);
                bassHit(bassNote, '16n', time + qn * 2, 1.0);
                bassHit(bassNote, '16n', time + qn * 3, 0.7);
                break;
            case 'tech':
                // Double-time chugs
                for (let i = 0; i < 8; i++) {
                    bassHit(bassNote, '16n', time + en * i, i % 2 === 0 ? 1.0 : 0.5);
                }
                break;
            case 'epic':
                bassHit(bassNote, '2n', time, 0.9);
                break;
            default:
                bassHit(bassNote, '4n', time, 0.8);
                bassHit(bassNote, '4n', time + qn * 2, 0.8);
        }
    }

    playBassPatternOddTime({ time, style, grid, bassNote, normalizedRoot, bassHit, approachNote }) {
        const pattern = style.drumPattern;
        const beat = grid.beat;
        const half = grid.half;
        const beats = grid.beats;
        const third = `${this.notes[(normalizedRoot + 4) % 12]}2`;
        const fifth = `${this.notes[(normalizedRoot + 7) % 12]}2`;
        const inBar = (beatIndex, offset = 0) => time + (beatIndex * beat) + offset;

        switch (pattern) {
            case 'ambient':
                bassHit(bassNote, grid.bar * 0.96, time, 0.56);
                break;
            case 'epic':
                bassHit(bassNote, Math.max(beat * 1.6, grid.bar * 0.52), time, 0.9);
                if (beats > 2) bassHit(fifth, beat * 0.92, inBar(Math.floor(beats / 2)), 0.66);
                break;
            case 'jazz': {
                for (let i = 0; i < beats; i++) {
                    let note = bassNote;
                    if (i % 4 === 1) note = third;
                    if (i % 4 === 2) note = fifth;
                    if (i === beats - 1) note = approachNote(normalizedRoot, Math.random() > 0.5);
                    bassHit(note, beat * 0.9, inBar(i), i === 0 ? 0.8 : 0.74);
                }
                break;
            }
            case 'fusion':
                for (let i = 0; i < beats; i++) {
                    const note = i % 2 === 0 ? bassNote : fifth;
                    bassHit(note, beat * 0.82, inBar(i), i === 0 ? 0.9 : 0.74);
                    if (Math.random() > 0.62 && i < beats - 1) {
                        bassHit(bassNote, beat * 0.28, inBar(i, half), 0.32);
                    }
                }
                break;
            case 'djent':
            case 'tech':
                for (let i = 0; i < beats; i++) {
                    bassHit(bassNote, beat * 0.46, inBar(i), i % 2 === 0 ? 1.0 : 0.8);
                    if (i < beats - 1) bassHit(bassNote, beat * 0.34, inBar(i, half), 0.58);
                }
                break;
            default:
                bassHit(bassNote, beat * 0.9, inBar(0), 0.84);
                if (beats > 2) bassHit(fifth, beat * 0.8, inBar(Math.floor(beats / 2)), 0.72);
                if (beats > 3) bassHit(approachNote(normalizedRoot, true), beat * 0.8, inBar(beats - 1), 0.68);
                break;
        }
    }

    playDrumPattern(time, style, isFirst) {
        const pattern = style.drumPattern;
        const grid = this.getTimingGrid();
        const qn = Tone.Time('4n').toSeconds();
        const en = Tone.Time('8n').toSeconds();
        const sn = Tone.Time('16n').toSeconds();
        const kick = (hitTime, duration = '8n', velocity = 0.85) => {
            this.drumKit.kick.triggerAttackRelease(
                'C1',
                duration,
                this.humanizeTime(hitTime, 0.002),
                this.humanizeVelocity(velocity, 0.06)
            );
        };
        const snare = (hitTime, duration = '8n', velocity = 0.78) => {
            this.drumKit.snare.triggerAttackRelease(
                duration,
                this.humanizeTime(hitTime, 0.0025),
                this.humanizeVelocity(velocity, 0.08)
            );
        };
        const hat = (hitTime, duration = '32n', velocity = 0.2) => {
            this.drumKit.hat.triggerAttackRelease(
                duration,
                this.humanizeTime(hitTime, 0.0025),
                this.humanizeVelocity(velocity, 0.1)
            );
        };
        const crash = (hitTime, duration = '4n', velocity = 0.45) => {
            this.drumKit.crash.triggerAttackRelease(
                duration,
                this.humanizeTime(hitTime, 0.003),
                this.humanizeVelocity(velocity, 0.06)
            );
        };

        if (!this.isFourFourGrid(grid)) {
            this.playDrumPatternOddTime({
                time,
                style,
                isFirst,
                grid,
                kick,
                snare,
                hat,
                crash
            });
            return;
        }

        switch (pattern) {
            case 'rock':
                // Standard rock: kick-hat-snare-hat
                kick(time, '8n', 0.88);
                snare(time + qn, '8n', 0.8);
                kick(time + qn * 2, '8n', 0.86);
                snare(time + qn * 3, '8n', 0.82);
                // Hi-hat 8ths
                for (let i = 0; i < 8; i++) {
                    hat(time + en * i, '32n', i % 2 === 0 ? 0.28 : 0.14);
                }
                if (isFirst) crash(time, '4n', 0.4);
                break;

            case 'tech':
                // Fast double kick, 16th hats
                kick(time, '16n', 0.96);
                kick(time + en, '16n', 0.87);
                kick(time + en * 1.5, '16n', 0.84);
                snare(time + qn, '8n', 0.76);
                kick(time + qn * 2, '16n', 0.94);
                snare(time + qn * 3, '8n', 0.8);
                for (let i = 0; i < 16; i++) {
                    hat(time + sn * i, '64n', 0.15);
                }
                if (isFirst) crash(time, '4n', 0.5);
                break;

            case 'fusion':
                // Syncopated, ghost notes, complex ride
                kick(time, '8n', 0.85);
                if (Math.random() > 0.4) kick(time + en * 1.5, '16n', 0.6);
                snare(time + qn, '8n', 0.73);
                if (Math.random() > 0.5) snare(time + qn * 1.5, '32n', 0.18);
                kick(time + qn * 2.5, '8n', 0.82);
                snare(time + qn * 3, '8n', 0.76);
                // Ride pattern
                for (let i = 0; i < 8; i++) {
                    hat(time + en * i, '16n', 0.2 + (i % 3 === 0 ? 0.15 : 0));
                }
                break;

            case 'jazz': {
                // Swing ride pattern + kick/snare comping
                // Ride: 1 + a, 2 + a, 3 + a, 4 + a (swung)
                const swing = this.swingAmount || 0.33;
                for (let beat = 0; beat < 4; beat++) {
                    const beatTime = time + qn * beat;
                    hat(beatTime, '8n', 0.32);
                    hat(beatTime + en * (1 + swing), '16n', 0.2);
                }
                // Sparse kick + snare comping
                kick(time, '8n', 0.5);
                if (Math.random() > 0.5) kick(time + qn * 2.5, '8n', 0.3);
                if (Math.random() > 0.6) snare(time + qn * (Math.random() > 0.5 ? 1 : 3), '16n', 0.15);
                break;
            }

            case 'ambient':
                // Minimal — just a soft hit every few bars
                if (isFirst) {
                    crash(time, '1n', 0.15);
                }
                if (Math.random() > 0.7) {
                    kick(time, '2n', 0.2);
                }
                break;

            case 'djent':
                // Djent: syncopated kicks, china hits
                kick(time, '16n', 1.0);
                kick(time + en * 1.5, '16n', 0.9);
                snare(time + qn, '8n', 0.8);
                kick(time + qn * 2, '16n', 1.0);
                kick(time + qn * 2 + en, '16n', 0.7);
                snare(time + qn * 3, '8n', 0.9);
                // Tight hat
                for (let i = 0; i < 16; i++) {
                    hat(time + sn * i, '64n', 0.1 + (i % 4 === 0 ? 0.15 : 0));
                }
                if (isFirst) crash(time, '4n', 0.6);
                break;

            case 'epic':
                // Cinematic impacts
                if (isFirst) crash(time, '4n', 0.5);
                kick(time, '2n', 0.9);
                kick(time + qn * 2, '4n', 0.7);
                // Timpani-style rumble
                if (Math.random() > 0.5) {
                    kick(time + qn * 3, '8n', 0.4);
                    kick(time + qn * 3.5, '8n', 0.5);
                }
                break;

            default:
                // Fallback: basic pattern
                kick(time, '8n', 0.84);
                snare(time + qn, '8n', 0.78);
                kick(time + qn * 2, '8n', 0.82);
                snare(time + qn * 3, '8n', 0.8);
                for (let i = 0; i < 8; i++) {
                    hat(time + en * i, '32n', 0.2);
                }
                break;
        }
    }

    playDrumPatternOddTime({ time, style, isFirst, grid, kick, snare, hat, crash }) {
        const pattern = style.drumPattern;
        const beats = grid.beats;
        const beat = grid.beat;
        const half = grid.half;
        const quarter = grid.quarter;
        const backbeatA = Math.max(1, Math.floor(beats / 2));
        const backbeatB = beats > 4 ? beats - 1 : Math.min(beats - 1, backbeatA + 2);
        const inBar = (beatIndex, offset = 0) => time + (beatIndex * beat) + offset;

        switch (pattern) {
            case 'ambient':
                if (isFirst) crash(time, `${grid.unit}n`, 0.14);
                if (Math.random() > 0.7) kick(time, beat * 1.6, 0.22);
                break;
            case 'epic':
                if (isFirst) crash(time, `${grid.unit}n`, 0.52);
                kick(inBar(0), beat * 1.3, 0.92);
                if (beats > 2) kick(inBar(backbeatA), beat, 0.72);
                if (beats > 4 && Math.random() > 0.55) kick(inBar(beats - 1), beat * 0.7, 0.46);
                break;
            case 'jazz': {
                const swing = this.swingAmount || 0.33;
                for (let b = 0; b < beats; b++) {
                    const beatTime = inBar(b);
                    hat(beatTime, `${grid.unit}n`, 0.31);
                    if (b < beats - 1) hat(beatTime + (half * (1 + (swing * 0.7))), `${Math.min(64, grid.unit * 2)}n`, 0.21);
                }
                kick(inBar(0), `${grid.unit}n`, 0.48);
                if (Math.random() > 0.45 && beats > 2) kick(inBar(Math.min(beats - 1, backbeatA + 1)), `${grid.unit}n`, 0.3);
                if (Math.random() > 0.62) snare(inBar(Math.random() > 0.5 ? backbeatA : backbeatB), `${Math.min(64, grid.unit * 2)}n`, 0.16);
                break;
            }
            case 'fusion':
                kick(inBar(0), `${grid.unit}n`, 0.86);
                if (Math.random() > 0.35 && beats > 2) kick(inBar(1, half), `${Math.min(64, grid.unit * 2)}n`, 0.58);
                snare(inBar(backbeatA), `${grid.unit}n`, 0.72);
                if (Math.random() > 0.52 && beats > 3) snare(inBar(backbeatA, half), `${Math.min(64, grid.unit * 4)}n`, 0.18);
                if (beats > 3) kick(inBar(Math.max(0, beats - 2), half), `${grid.unit}n`, 0.82);
                for (let b = 0; b < beats; b++) {
                    hat(inBar(b), `${Math.min(64, grid.unit * 2)}n`, 0.2 + (b % 3 === 0 ? 0.13 : 0));
                    if (Math.random() > 0.4) hat(inBar(b, half), `${Math.min(64, grid.unit * 2)}n`, 0.15);
                }
                break;
            case 'tech':
            case 'djent':
                for (let b = 0; b < beats; b++) {
                    kick(inBar(b), `${Math.min(64, grid.unit * 2)}n`, b % 2 === 0 ? 0.96 : 0.82);
                    if (b < beats - 1) kick(inBar(b, half), `${Math.min(64, grid.unit * 2)}n`, 0.72);
                }
                snare(inBar(backbeatA), `${grid.unit}n`, 0.84);
                if (backbeatB !== backbeatA) snare(inBar(backbeatB), `${grid.unit}n`, 0.88);
                for (let b = 0; b < beats; b++) {
                    for (let s = 0; s < 4; s++) {
                        hat(inBar(b, quarter * s), `${Math.min(128, grid.unit * 8)}n`, 0.1 + (s === 0 ? 0.12 : 0));
                    }
                }
                if (isFirst) crash(time, `${grid.unit}n`, 0.58);
                break;
            default:
                kick(inBar(0), `${grid.unit}n`, 0.86);
                if (beats > 2) kick(inBar(Math.floor(beats / 2)), `${grid.unit}n`, 0.82);
                snare(inBar(backbeatA), `${grid.unit}n`, 0.8);
                if (backbeatB !== backbeatA) snare(inBar(backbeatB), `${grid.unit}n`, 0.78);
                for (let b = 0; b < beats; b++) {
                    hat(inBar(b), `${Math.min(64, grid.unit * 2)}n`, 0.26);
                    if (b < beats - 1) hat(inBar(b, half), `${Math.min(64, grid.unit * 2)}n`, 0.16);
                }
                if (isFirst) crash(time, `${grid.unit}n`, 0.4);
                break;
        }
    }

    // ========================================
    // PROGRESSION GENERATION (V20 ADVANCED)
    // ========================================
    generateProgression(length = this.progressionLength) {
        this.progressionLength = Math.max(4, Math.min(16, parseInt(length, 10) || this.progressionLength));
        this.syncLengthUI();
        this.key = document.getElementById('KeySelect')?.value || "C";
        this.mode = document.getElementById('ModeSelect')?.value || "ionian";
        this.harmonicIntent = document.getElementById('ComplexitySelect')?.value || "stable";

        // Sparkle feedback
        const btn = document.getElementById('BtnInspire');
        if (btn) {
            btn.classList.add('is-active');
            setTimeout(() => btn.classList.remove('is-active'), 800);
        }


        const scaleIntervals = this.scales[this.mode];
        const keyIndex = this.notes.indexOf(this.key);
        const scaleNotes = scaleIntervals.map(i => this.notes[(keyIndex + i) % 12]);
        const qualityMap = this.qualities[this.mode] || this.qualities.ionian;

        this.progression = [];

        // Advanced Harmonic Templates
        const templates = {
            stable: {
                ionian: [[0, 3, 4, 3], [0, 5, 3, 4], [0, 4, 5, 3]],
                aeolian: [[0, 5, 6, 4], [0, 3, 6, 4], [0, 2, 4, 3]]
            },
            sophisticated: {
                ionian: [
                    [0, 5, 3, 4], // I-vi-IV-V
                    [0, 3, 5, 6], // I-IV-bVI-bVII (Modal Interchange)
                    [0, 4, 1, 3]  // I-III-ii-IV
                ],
                aeolian: [
                    [0, 5, 3, 4], // i-VI-iv-v
                    [0, 6, 5, 4], // i-bVII-bVI-V
                    [0, 3, 4, 1]  // i-iv-v-ii°
                ]
            },
            pro: {
                ionian: [
                    [0, 5, 1, 4], // 2-5-1 variations
                    [0, 6, 1, 4], // bVII-I-V
                    [0, 3, 5, 4], // I-IV-bVI-V
                    [1, 4, 0, 5], // ii-V-I-vi
                ],
                aeolian: [
                    [1, 4, 0, 5], // ii°-V-i-VI
                    [0, 1, 3, 4], // i-ii°-iv-V
                    [0, 5, 1, 4]  // i-VI-ii°-V
                ]
            }
        };

        const category = templates[this.harmonicIntent] || templates.stable;
        const currentTemplates = category[this.mode] || category.ionian || [[0, 3, 4, 3]];
        const template = currentTemplates[Math.floor(Math.random() * currentTemplates.length)];

        for (let i = 0; i < this.progressionLength; i++) {
            const degree = template[i % template.length];

            let quality = qualityMap[degree] || qualityMap[0];

            // Smart Harmony Overrides
            if (this.harmonicIntent === 'pro') {
                if (this.mode === 'aeolian' && degree === 4) quality = 'dom7'; // Major V in minor
                if (this.mode === 'ionian' && degree === 5) quality = 'min7'; // Modal interchange iim7
            }

            // Advanced Extension Logic
            let extensions = [];
            if (this.harmonicIntent === 'sophisticated') {
                extensions = Math.random() > 0.4 ? ['9'] : [];
            } else if (this.harmonicIntent === 'pro') {
                const proExts = [['9'], ['9', '11'], ['9', '13'], ['b9'], ['#11']];
                extensions = proExts[Math.floor(Math.random() * proExts.length)];
            }

            this.progression.push({
                root: scaleNotes[degree] || scaleNotes[0],
                quality: quality,
                degree: degree + 1,
                bars: 2,
                extensions: extensions,
                inversion: this.autoInvert ? Math.floor(Math.random() * 3) : 0
            });
        }

        this.recomputeChordDegrees();
        this.renderChords();
        this.restartIfPlaying();
    }

    shuffleProgression() {
        // Shuffle existing progression order
        for (let i = this.progression.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [this.progression[i], this.progression[j]] = [this.progression[j], this.progression[i]];
        }
        this.recomputeChordDegrees();
        this.renderChords();
        this.restartIfPlaying();
    }

    addChord() {
        const last = this.progression[this.progression.length - 1];
        this.progression.push({
            root: last?.root || this.key,
            quality: last?.quality || 'Maj7',
            degree: last?.degree || 1,
            bars: 2
        });
        this.progressionLength = Math.max(4, Math.min(16, this.progression.length));
        this.syncLengthUI();
        this.recomputeChordDegrees();
        this.renderChords();
        this.restartIfPlaying();
    }

    // ========================================
    // CHORD THEORY (V20 STUDIO)
    // ========================================
    getChordNotes(root, quality, extensions = [], inversion = 0) {
        let intervals = [0, 4, 7]; // Major default
        const q = quality.toLowerCase();

        if (q.includes('min') || q.includes('m') && !q.includes('maj') && !q.includes('dom')) {
            intervals = [0, 3, 7];
        }
        if (q.includes('dim') || q.includes('°')) {
            intervals = [0, 3, 6];
        }
        if (q.includes('ø') || q.includes('m7b5')) {
            intervals = [0, 3, 6];
        }
        if (q.includes('aug') || q.includes('+') || q.includes('#5')) {
            intervals = [0, 4, 8];
        }
        if (q.includes('sus4')) {
            intervals = [0, 5, 7];
        }
        if (q.includes('sus2')) {
            intervals = [0, 2, 7];
        }

        // 7th logic
        if (q.includes('7')) {
            if (q.includes('maj7') || q.includes('min(maj7)') || q.includes('maj7#5')) {
                intervals.push(11);
            } else if (q.includes('dim7')) {
                intervals.push(9);
            } else {
                intervals.push(10);
            }
        }

        // Add extensions
        if (extensions.includes('9')) intervals.push(14);
        if (extensions.includes('b9')) intervals.push(13);
        if (extensions.includes('#9')) intervals.push(15);
        if (extensions.includes('11')) intervals.push(17);
        if (extensions.includes('#11')) intervals.push(18);
        if (extensions.includes('13')) intervals.push(21);
        if (extensions.includes('b13')) intervals.push(20);

        if (typeof Tone === 'undefined' || !Tone.Frequency) {
            return intervals.map(i => `${this.transposeNoteName(root, i)}3`);
        }

        // Map to notes
        let notes = intervals.map(i => Tone.Frequency(root + "3").transpose(i));

        // Apply Inversion
        if (inversion > 0 && notes.length > inversion) {
            for (let i = 0; i < inversion; i++) {
                const note = notes.shift();
                notes.push(note.transpose(12));
            }
        }

        // Voice Leading Algorithm (V20)
        // If we have last played notes, shift current notes to minimize mean distance
        if (this.enableVoiceLeading && this.lastPlayedNotes) {
            const targetMean = this.lastPlayedNotes.reduce((acc, n) => acc + n.toFrequency(), 0) / this.lastPlayedNotes.length;

            // Try different octave shifts to get closest to target mean
            let bestDiff = Infinity;
            let bestNotes = notes;

            [-12, 0, 12].forEach(shift => {
                const shifted = notes.map(n => n.transpose(shift));
                const currentMean = shifted.reduce((acc, n) => acc + n.toFrequency(), 0) / shifted.length;
                const diff = Math.abs(currentMean - targetMean);
                if (diff < bestDiff) {
                    bestDiff = diff;
                    bestNotes = shifted;
                }
            });
            notes = bestNotes;
        }

        this.lastPlayedNotes = notes;
        return notes.map(n => n.toNote());
    }

    // ========================================
    // UI RENDERING
    // ========================================
    renderChords() {
        const lane = document.getElementById('ChordList');
        if (!lane) return;
        lane.innerHTML = '';

        this.progression.forEach((chord, index) => {
            const card = document.createElement('div');
            card.className = 'chord-block';
            card.dataset.index = index;

            const roman = this.getRomanNumeral(chord);
            const scale = this.getSuggestedScale(chord);
            const extensionLabel = Array.isArray(chord.extensions) && chord.extensions.length > 0
                ? chord.extensions[0]
                : '';

            // Quality-based styling
            let qualityCat = 'maj';
            const q = chord.quality.toLowerCase();
            if (q.includes('min') || q.includes('m7')) qualityCat = 'min';
            else if (q.includes('dim') || q.includes('ø')) qualityCat = 'dim';
            else if (q.includes('dom')) qualityCat = 'dom7';
            else if (q.includes('maj7')) qualityCat = 'maj7';
            card.dataset.quality = qualityCat;

            // Width based on bars
            const chordWidth = Math.max(180, chord.bars * 100);
            card.style.flex = `0 0 ${chordWidth}px`;
            card.style.minWidth = `${chordWidth}px`;

            const chordName = `${chord.root}${extensionLabel} ${chord.quality}`.trim();
            const inversionNames = ['root position', 'first inversion', 'second inversion', 'third inversion'];
            const makeText = (tag, className, text) => {
                const node = document.createElement(tag);
                node.className = className;
                node.textContent = text;
                return node;
            };

            const header = document.createElement('div');
            header.className = 'chord-header';
            header.appendChild(makeText('span', 'chord-numeral', roman));

            const inversionButton = document.createElement('button');
            inversionButton.type = 'button';
            inversionButton.className = 'chord-inv-badge';
            inversionButton.dataset.action = 'cycle-inv';
            inversionButton.textContent = ['R', '1i', '2i', '3i'][chord.inversion || 0];
            inversionButton.setAttribute('aria-label', `Change inversion for ${chordName}. Current: ${inversionNames[chord.inversion || 0]}`);
            header.appendChild(inversionButton);

            const previewButton = document.createElement('button');
            previewButton.type = 'button';
            previewButton.className = 'chord-preview';
            previewButton.setAttribute('aria-label', `Preview ${chordName}`);

            const main = document.createElement('span');
            main.className = 'chord-main';
            main.append(
                makeText('span', 'chord-root', `${chord.root}${extensionLabel}`),
                makeText('span', 'chord-quality', chord.quality)
            );

            const analysis = document.createElement('span');
            analysis.className = 'chord-analysis';
            analysis.append(
                makeText('span', 'scale-tag', 'Suggested scale'),
                makeText('span', 'scale-suggestion', scale)
            );
            previewButton.append(main, analysis);

            const bars = makeText('div', 'chord-bars', `${chord.bars} bar${chord.bars > 1 ? 's' : ''}`);
            const controls = document.createElement('div');
            controls.className = 'chord-controls';
            [
                ['bars-down', '−', `Decrease ${chordName} length`],
                ['bars-up', '+', `Increase ${chordName} length`],
                ['delete', '×', `Remove ${chordName}`]
            ].forEach(([action, text, label]) => {
                const button = document.createElement('button');
                button.type = 'button';
                button.className = `chord-ctrl-btn${action === 'delete' ? ' delete' : ''}`;
                button.dataset.action = action;
                button.textContent = text;
                button.setAttribute('aria-label', label);
                if (action === 'bars-down' && chord.bars <= 1) button.disabled = true;
                if (action === 'bars-up' && chord.bars >= 8) button.disabled = true;
                if (action === 'delete' && this.progression.length <= 1) button.disabled = true;
                controls.appendChild(button);
            });

            card.append(header, previewButton, bars, controls);
            previewButton.addEventListener('click', () => this.previewChord(chord));

            // Cycle Inversion Click
            inversionButton.addEventListener('click', (e) => {
                e.stopPropagation();
                chord.inversion = ((chord.inversion || 0) + 1) % 4;
                this.renderChords();
                this.previewChord(chord);
                this.restartIfPlaying();
            });

            // Control buttons
            card.querySelectorAll('.chord-ctrl-btn').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const action = btn.dataset.action;
                    if (action === 'bars-up' && chord.bars < 8) {
                        chord.bars++;
                        this.renderChords();
                        this.restartIfPlaying();
                    } else if (action === 'bars-down' && chord.bars > 1) {
                        chord.bars--;
                        this.renderChords();
                        this.restartIfPlaying();
                    } else if (action === 'delete' && this.progression.length > 1) {
                        this.progression.splice(index, 1);
                        this.progressionLength = Math.max(4, Math.min(16, this.progression.length));
                        this.syncLengthUI();
                        this.recomputeChordDegrees();
                        this.renderChords();
                        this.restartIfPlaying();
                    }
                });
            });

            lane.appendChild(card);
        });

        // Add button in lane
        const addBtn = document.createElement('button');
        addBtn.className = 'add-chord-btn';
        addBtn.innerHTML = '+';
        addBtn.addEventListener('click', () => this.addChord());
        lane.appendChild(addBtn);

        this.updateBarRuler();
    }

    updateBarRuler() {
        const ruler = document.getElementById('BarRuler');
        if (!ruler) return;

        let totalBars = 0;
        this.progression.forEach(c => totalBars += c.bars);

        ruler.innerHTML = '';
        for (let i = 1; i <= totalBars; i++) {
            const mark = document.createElement('span');
            mark.className = 'bar-mark';
            mark.textContent = i;
            ruler.appendChild(mark);
        }
    }

    async previewChord(chord) {
        await this.initAudio();
        const notes = this.getChordNotes(chord.root, chord.quality, chord.extensions || [], chord.inversion || 0);
        this.synths.pad.triggerAttackRelease(notes, '2n');
    }

    // ========================================
    // VISUAL FEEDBACK
    // ========================================
    updateTransportUI(playing) {
        const btn = document.getElementById('BtnPlay');
        const status = document.getElementById('StatusText');
        const playhead = document.getElementById('Playhead');

        if (playing) {
            btn?.classList.add('playing');
            btn?.setAttribute('aria-pressed', 'true');
            btn?.setAttribute('aria-label', 'Pause');
            btn?.querySelector('.play-icon')?.style.setProperty('display', 'none');
            btn?.querySelector('.pause-icon')?.style.setProperty('display', 'block');
            if (status) status.textContent = 'PLAYING';
            playhead?.classList.add('active');
        } else {
            btn?.classList.remove('playing');
            btn?.setAttribute('aria-pressed', 'false');
            btn?.setAttribute('aria-label', 'Play');
            btn?.querySelector('.play-icon')?.style.setProperty('display', 'block');
            btn?.querySelector('.pause-icon')?.style.setProperty('display', 'none');
            if (status) status.textContent = 'READY';
            playhead?.classList.remove('active');
        }
    }

    updatePlayhead(chordIndex, barInChord) {
        // Highlight active chord
        document.querySelectorAll('.chord-block').forEach((el, i) => {
            el.classList.toggle('playing', i === chordIndex);
        });

        // Move playhead
        const playhead = document.getElementById('Playhead');
        const track = document.getElementById('TimelineTrack');
        const chordCards = document.querySelectorAll('.chord-block');

        if (playhead && track && chordCards[chordIndex]) {
            const card = chordCards[chordIndex];
            const cardRect = card.getBoundingClientRect();
            const trackRect = track.getBoundingClientRect();
            const barWidth = cardRect.width / this.progression[chordIndex].bars;
            const offsetX = card.offsetLeft + (barInChord * barWidth);
            playhead.style.left = `${offsetX + 16}px`;
        }
    }

    clearPlayhead() {
        document.querySelectorAll('.chord-block').forEach(el => el.classList.remove('playing'));
        const playhead = document.getElementById('Playhead');
        if (playhead) playhead.style.left = '16px';
    }

    startVUAnimation() {
        this.vuAnimationId = requestAnimationFrame(() => this.animateVU());
    }

    stopVUAnimation() {
        if (this.vuAnimationId) cancelAnimationFrame(this.vuAnimationId);
        this.vuAnimationId = null;
        ['VuPad', 'VuBass', 'VuDrums'].forEach((id) => {
            const el = document.getElementById(id);
            if (el) el.style.height = '0%';
        });
    }

    animateVU() {
        if (!this.isPlaying) return;

        ['Pad', 'Bass', 'Drums'].forEach(channel => {
            const analyzer = this.fx[`analyzer${channel}`];
            const vuFill = document.getElementById(`Vu${channel}`);

            if (analyzer && vuFill) {
                const values = analyzer.getValue();
                const rms = Math.sqrt(values.reduce((sum, v) => sum + v * v, 0) / values.length);
                const level = Math.min(100, rms * 500);
                vuFill.style.height = `${level}%`;
            }
        });

        this.vuAnimationId = requestAnimationFrame(() => this.animateVU());
    }

    // ========================================
    // INSPIRE & RANDOMIZE
    // ========================================
    inspire() {
        // Random key
        this.key = this.notes[Math.floor(Math.random() * this.notes.length)];
        const keySelect = document.getElementById('KeySelect');
        if (keySelect) keySelect.value = this.key;

        // Random mode
        const modeOptions = Array.from(document.getElementById('ModeSelect')?.options || []).map(o => o.value).filter(Boolean);
        const modes = modeOptions.length > 0 ? modeOptions : Object.keys(this.scales);
        this.mode = modes[Math.floor(Math.random() * modes.length)];
        const modeSelect = document.getElementById('ModeSelect');
        if (modeSelect) modeSelect.value = this.mode;

        // Random style
        const styles = Object.keys(this.styleConfigs);
        this.style = styles[Math.floor(Math.random() * styles.length)];
        const styleSelect = document.getElementById('StyleSelect');
        if (styleSelect) styleSelect.value = this.style;
        this.syncVibeCards();

        // Tempo in style range
        const config = this.styleConfigs[this.style];
        this.bpm = Math.floor(Math.random() * (config.tempo[1] - config.tempo[0])) + config.tempo[0];
        const tempoSlider = document.getElementById('TempoSlider');
        if (tempoSlider) tempoSlider.value = this.bpm;
        const tempoVal = document.getElementById('TempoVal');
        if (tempoVal) tempoVal.textContent = this.bpm;

        this.generateProgression(this.progressionLength);
        this.applyStyle();

        // Auto play
        if (!this.isPlaying) {
            this.play();
        }

        this.setStatus('New inspiration loaded', 1600);
        this._scheduleSave?.();
    }

    // ========================================
    // EXPORT
    // ========================================
    exportMIDI() {
        if (typeof MidiWriter === 'undefined' || typeof Tone === 'undefined' || !Tone.Frequency) {
            alert('MIDI export tools are still loading. Please try again in a moment.');
            return;
        }

        const track = new MidiWriter.Track();
        track.setTempo(this.bpm);

        // MidiWriter duration strings: '1' = whole, '2' = half, '4' = quarter, '8' = eighth
        // chord.bars is number of whole bars; each bar = 1 whole note in 4/4
        const barsToDuration = (bars) => {
            if (bars >= 4) return ['1', '1', '1', '1'];
            if (bars === 3) return ['1', '1', '1'];
            if (bars === 2) return ['1', '1'];
            return ['1']; // 1 bar = 1 whole note
        };

        this.progression.forEach(chord => {
            const notes = this.getChordNotes(chord.root, chord.quality);
            const midiNotes = notes.map(n => {
                try { return Tone.Frequency(n).toMidi(); } catch (e) { return 60; }
            });
            const durations = barsToDuration(chord.bars);

            durations.forEach(dur => {
                track.addEvent(new MidiWriter.NoteEvent({
                    pitch: midiNotes,
                    duration: dur,
                    velocity: 80
                }));
            });
        });

        const writer = new MidiWriter.Writer(track);
        const blob = new Blob([writer.buildFile()], { type: 'audio/midi' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `proggen_${this.key}_${this.mode}_${Date.now()}.mid`;
        a.click();

        setTimeout(() => URL.revokeObjectURL(url), 10000);
        this.setStatus('MIDI exported', 2000);
    }

    shareLink() {
        const state = {
            k: this.key,
            m: this.mode,
            s: this.style,
            ps: this.padSound,
            t: this.bpm,
            l: this.progressionLength,
            ts: this.timeSignature,
            sw: this.swingAmount,
            rs: this.rhythmStyle,
            hi: this.harmonicIntent,
            p: this.progression.map(c => ({
                r: c.root,
                q: c.quality,
                b: c.bars,
                i: c.inversion || 0,
                e: c.extensions || [],
                d: c.degree || 1
            }))
        };

        const bytes = new TextEncoder().encode(JSON.stringify(state));
        let binary = '';
        bytes.forEach(byte => { binary += String.fromCharCode(byte); });
        const encoded = btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
        const url = `${window.location.origin}${window.location.pathname}?seed=${encoded}`;

        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(url).then(() => {
                this.setStatus('Studio link copied', 2000);
            }).catch(() => {
                window.prompt('Copy this studio link:', url);
                this.setStatus('Studio link ready to copy', 2000);
            });
            return;
        }

        window.prompt('Copy this studio link:', url);
        this.setStatus('Studio link ready to copy', 2000);
    }

    parseSeed() {
        const params = new URLSearchParams(window.location.search);
        const seed = params.get('seed');
        if (!seed) return false;
        if (seed.length > 20000) return false;

        try {
            const normalizedSeed = seed.replace(/-/g, '+').replace(/_/g, '/');
            const paddedSeed = normalizedSeed + '='.repeat((4 - normalizedSeed.length % 4) % 4);
            const bytes = Uint8Array.from(atob(paddedSeed), char => char.charCodeAt(0));
            const state = JSON.parse(new TextDecoder().decode(bytes));
            if (!state || typeof state !== 'object' || Array.isArray(state)) throw new Error('Invalid shared state');
            this.key = this.notes.includes(state.k) ? state.k : 'C';
            this.mode = this.scales[state.m] ? state.m : 'ionian';
            this.style = this.styleConfigs[state.s] ? state.s : 'prog_rock';
            this.padSound = this.padSoundConfigs[state.ps] ? state.ps : 'warm_pad';
            this.bpm = Math.max(40, Math.min(220, Number(state.t) || 110));
            this.progressionLength = Math.max(4, Math.min(16, state.l || this.progressionLength));
            this.rhythmStyle = ['strum', 'arpeggio', 'jazz_comp', 'swell', 'syncopated', 'simple'].includes(state.rs) ? state.rs : 'strum';
            this.harmonicIntent = ['stable', 'sophisticated', 'pro'].includes(state.hi) ? state.hi : 'stable';
            if (Array.isArray(state.ts)) {
                const upper = [3, 4, 5, 6, 7].includes(Number(state.ts[0])) ? Number(state.ts[0]) : 4;
                const lower = [4, 8].includes(Number(state.ts[1])) ? Number(state.ts[1]) : 4;
                this.timeSignature = [upper, lower];
            }
            if (state.sw !== undefined) this.swingAmount = Math.max(0, Math.min(1, Number(state.sw) || 0));

            if (Array.isArray(state.p)) {
                const allowedQualities = new Set(Object.values(this.qualities).flat().concat(['Maj', 'min', 'ø7']));
                this.progression = state.p.slice(0, 16).map(c => ({
                    root: /^[A-G](?:#|b)?$/.test(c.r || '') ? c.r : this.key,
                    quality: allowedQualities.has(c.q) ? c.q : 'Maj7',
                    bars: Math.max(1, Math.min(8, Number(c.b) || 1)),
                    inversion: Math.max(0, Math.min(3, Number(c.i) || 0)),
                    extensions: Array.isArray(c.e) ? c.e.filter(value => ['7', '9', '11', '13'].includes(String(value))).slice(0, 4) : [],
                    degree: Math.max(1, Math.min(7, Number(c.d) || 1))
                }));
            }

            this.recomputeChordDegrees();
            this.syncAllUI();
            this.renderChords();
            this.applyStyle();
            this.setStatus('Loaded shared studio state', 1800);
            return true;
        } catch (e) {
            console.error('[Generator] Seed parsing failed', e);
            return false;
        }
    }

    renderSavedTracks() {
        const container = document.getElementById('SavedTracksList');
        if (!container) return;

        const tracks = this.getSavedTracksList();
        if (tracks.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'empty-favs';
            empty.textContent = 'No saved tracks yet.';
            container.replaceChildren(empty);
            return;
        }

        container.replaceChildren();
        tracks.forEach(track => {
            if (!track || typeof track !== 'object') return;
            const div = document.createElement('div');
            div.className = 'fav-item sa-card-mini';
            const dateStr = new Date(track.savedAt).toLocaleDateString();
            const hash = /^[A-Za-z0-9]{6}$/.test(String(track.hash || '')) ? String(track.hash) : '';
            const trackName = this.cleanTrackName(track.name, 'Untitled track');
            const info = document.createElement('div');
            info.className = 'fav-info';
            const name = document.createElement('span');
            name.className = 'fav-name';
            name.textContent = trackName;
            const details = document.createElement('span');
            details.className = 'fav-meta';
            details.textContent = `${Number(track.bpm) || 110} BPM • ${String(track.style || 'prog_rock').replace(/_/g, ' ')} • ${String(track.key || 'C')} ${String(track.mode || 'ionian')}`;
            const saved = document.createElement('span');
            saved.className = 'fav-meta';
            saved.textContent = `${hash ? `#${hash} • ` : ''}${dateStr === 'Invalid Date' ? 'Saved track' : dateStr}`;
            info.append(name, details, saved);

            const actions = document.createElement('div');
            actions.className = 'fav-actions';
            const loadButton = document.createElement('button');
            loadButton.type = 'button';
            loadButton.className = 'fav-load';
            loadButton.textContent = 'Load';
            loadButton.setAttribute('aria-label', `Load ${trackName}`);
            const deleteButton = document.createElement('button');
            deleteButton.type = 'button';
            deleteButton.className = 'fav-delete';
            deleteButton.textContent = '×';
            deleteButton.setAttribute('aria-label', `Delete ${trackName}`);
            actions.append(loadButton, deleteButton);
            div.append(info, actions);

            loadButton.addEventListener('click', () => {
                if (hash && this.loadTrack(hash)) {
                    this.setStatus(`Loaded: ${trackName}`, 2000);
                }
            });
            deleteButton.addEventListener('click', () => {
                if (hash) this.deleteTrack(hash);
                this.renderSavedTracks();
            });
            container.appendChild(div);
        });
    }

    toggleFavorite() {
        const state = {
            id: Date.now(),
            name: `${this.key} ${this.mode.charAt(0).toUpperCase() + this.mode.slice(1)} Jam`,
            key: this.key,
            mode: this.mode,
            progression: JSON.parse(JSON.stringify(this.progression)),
            bpm: this.bpm,
            style: this.style,
            progressionLength: this.progressionLength
        };

        const favs = this.getFavorites();
        favs.unshift(state);
        this.saveFavorites(favs.slice(0, 25));

        this.setStatus('Added to favorites', 1800);
        this.renderFavorites();
    }

    loadFavorites() {
        this.renderFavorites();
    }

    renderFavorites() {
        const container = document.getElementById('FavoritesList');
        if (!container) return;

        const favs = this.getFavorites();
        if (favs.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'empty-favs';
            empty.textContent = 'No saved tracks yet.';
            container.replaceChildren(empty);
            return;
        }

        container.replaceChildren();
        favs.forEach((fav, index) => {
            if (!fav || typeof fav !== 'object') return;
            const div = document.createElement('div');
            div.className = 'fav-item sa-card-mini';
            const favName = this.cleanTrackName(fav.name, 'Saved progression');
            const info = document.createElement('div');
            info.className = 'fav-info';
            const name = document.createElement('span');
            name.className = 'fav-name';
            name.textContent = favName;
            const details = document.createElement('span');
            details.className = 'fav-meta';
            details.textContent = `${Number(fav.bpm) || 110} BPM • ${String(fav.style || 'prog_rock').replace(/_/g, ' ')}`;
            info.append(name, details);

            const actions = document.createElement('div');
            actions.className = 'fav-actions';
            const loadButton = document.createElement('button');
            loadButton.type = 'button';
            loadButton.className = 'fav-load';
            loadButton.textContent = 'Load';
            loadButton.setAttribute('aria-label', `Load ${favName}`);
            const deleteButton = document.createElement('button');
            deleteButton.type = 'button';
            deleteButton.className = 'fav-delete';
            deleteButton.textContent = '×';
            deleteButton.setAttribute('aria-label', `Delete ${favName}`);
            actions.append(loadButton, deleteButton);
            div.append(info, actions);

            loadButton.addEventListener('click', () => {
                const progression = this.sanitizeProgression(fav.progression);
                if (progression.length === 0) {
                    this.setStatus('Saved progression could not be loaded', 1800);
                    return;
                }
                this.key = fav.key;
                this.mode = fav.mode;
                this.progression = progression;
                this.bpm = fav.bpm;
                this.style = fav.style || 'prog_rock';
                this.progressionLength = Math.max(4, Math.min(16, fav.progressionLength || this.progression.length));
                this.recomputeChordDegrees();

                this.syncAllUI();

                this.renderChords();
                this.applyStyle();
                this.setStatus(`Loaded favorite: ${favName}`, 1800);
            });

            deleteButton.addEventListener('click', () => {
                const next = this.getFavorites();
                next.splice(index, 1);
                this.saveFavorites(next);
                this.renderFavorites();
            });

            container.appendChild(div);
        });
    }

    captureState() {
        return {
            key: this.key,
            mode: this.mode,
            style: this.style,
            padSound: this.padSound,
            bpm: this.bpm,
            progressionLength: this.progressionLength,
            rhythmStyle: this.rhythmStyle,
            harmonicIntent: this.harmonicIntent,
            timeSignature: [...this.timeSignature],
            swingAmount: this.swingAmount,
            enableVoiceLeading: this.enableVoiceLeading,
            autoInvert: this.autoInvert,
            progression: JSON.parse(JSON.stringify(this.progression))
        };
    }

    applyState(state) {
        if (!state) return;

        this.key = state.key || this.key;
        this.mode = state.mode || this.mode;
        this.style = this.styleConfigs[state.style] ? state.style : this.style;
        this.padSound = state.padSound || this.padSound;
        this.bpm = state.bpm || this.bpm;
        this.progressionLength = Math.max(4, Math.min(16, state.progressionLength || this.progressionLength));
        this.rhythmStyle = state.rhythmStyle || this.rhythmStyle;
        this.harmonicIntent = state.harmonicIntent || this.harmonicIntent;
        this.timeSignature = Array.isArray(state.timeSignature) ? [...state.timeSignature] : this.timeSignature;
        this.swingAmount = typeof state.swingAmount === 'number' ? state.swingAmount : this.swingAmount;
        this.enableVoiceLeading = state.enableVoiceLeading !== false;
        this.autoInvert = !!state.autoInvert;

        const progression = this.sanitizeProgression(state.progression);
        if (progression.length > 0) {
            this.progression = progression;
        }

        this.recomputeChordDegrees();
        this.syncAllUI();
        this.renderChords();

        if ((typeof Tone !== 'undefined' && Tone.Transport)) {
            Tone.Transport.bpm.value = this.bpm;
            Tone.Transport.timeSignature = this.timeSignature;
            Tone.Transport.swing = this.swingAmount;
            Tone.Transport.swingSubdivision = this.getSwingSubdivision();
        }

        if (this.audioReady) {
            this.rebuildPadSynth();
            this.applyStyle();
            this.updateMix();
        }
        this.restartIfPlaying();
    }

    withSeededRandom(seed, callback) {
        const originalRandom = Math.random;
        let state = seed >>> 0;

        Math.random = () => {
            state = (1664525 * state + 1013904223) >>> 0;
            return state / 4294967296;
        };

        try {
            return callback();
        } finally {
            Math.random = originalRandom;
        }
    }

    dateKeyToday() {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }

    seedFromDateKey(dateKey) {
        let hash = 2166136261;
        for (let i = 0; i < dateKey.length; i++) {
            hash ^= dateKey.charCodeAt(i);
            hash = Math.imul(hash, 16777619);
        }
        return hash >>> 0;
    }

    loadDailyProgression(showStatus = true) {
        const dateKey = this.dateKeyToday();
        let dailyPayload = null;
        try {
            dailyPayload = JSON.parse(window.flPreferenceStorage.getItem(this.dailyStorageKey) || 'null');
        } catch (e) {
            dailyPayload = null;
        }

        if (dailyPayload?.date === dateKey && dailyPayload?.state) {
            this.applyState(dailyPayload.state);
            if (showStatus) this.setStatus(`Daily progression loaded (${dateKey})`, 2200);
            return;
        }

        const modeOptions = Array.from(document.getElementById('ModeSelect')?.options || []).map(o => o.value).filter(Boolean);
        const styleOptions = Array.from(document.getElementById('StyleSelect')?.options || []).map(o => o.value).filter(Boolean);
        const intents = ['stable', 'sophisticated', 'pro'];

        this.withSeededRandom(this.seedFromDateKey(dateKey), () => {
            const pick = (arr, fallback) => (arr.length > 0 ? arr[Math.floor(Math.random() * arr.length)] : fallback);
            this.key = pick(this.notes, 'C');
            this.mode = pick(modeOptions, 'ionian');
            this.style = pick(styleOptions, 'prog_rock');
            this.harmonicIntent = pick(intents, 'sophisticated');
            this.progressionLength = 4 + Math.floor(Math.random() * 9);

            const styleConfig = this.styleConfigs[this.style] || this.styleConfigs.prog_rock;
            this.bpm = Math.floor(styleConfig.tempo[0] + (Math.random() * (styleConfig.tempo[1] - styleConfig.tempo[0])));

            this.syncAllUI();
            this.generateProgression(this.progressionLength);
            this.applyStyle();
        });

        try {
            window.flPreferenceStorage.setItem(this.dailyStorageKey, JSON.stringify({
                date: dateKey,
                state: this.captureState()
            }));
        } catch (e) {
            /* local storage unavailable */
        }

        if (showStatus) this.setStatus(`New daily progression ready (${dateKey})`, 2200);
    }

    tapTempo() {
        const now = performance.now();
        this.tapTempoTimes = this.tapTempoTimes.filter(t => now - t < 2400);
        this.tapTempoTimes.push(now);

        const tapBtn = document.getElementById('BtnTapTempo');
        tapBtn?.classList.add('is-listening');
        setTimeout(() => tapBtn?.classList.remove('is-listening'), 220);

        if (this.tapTempoTimes.length < 2) {
            this.setStatus('Tap again to set tempo', 900);
            return;
        }

        const intervals = [];
        for (let i = 1; i < this.tapTempoTimes.length; i++) {
            intervals.push(this.tapTempoTimes[i] - this.tapTempoTimes[i - 1]);
        }

        const averageMs = intervals.reduce((sum, val) => sum + val, 0) / intervals.length;
        const nextBpm = Math.max(40, Math.min(240, Math.round(60000 / averageMs)));
        if (!Number.isFinite(nextBpm)) return;

        this.bpm = nextBpm;
        const tempoSlider = document.getElementById('TempoSlider');
        if (tempoSlider) tempoSlider.value = `${this.bpm}`;
        const tempoVal = document.getElementById('TempoVal');
        if (tempoVal) tempoVal.textContent = `${this.bpm}`;
        if ((typeof Tone !== 'undefined' && Tone.Transport)) Tone.Transport.bpm.rampTo(this.bpm, 0.08);
        this.setStatus(`Tempo set: ${this.bpm} BPM`, 1200);
    }

    transposeProgression(semitones) {
        if (!this.progression.length || !Number.isFinite(semitones)) return;

        this.key = this.transposeNoteName(this.key, semitones);
        this.progression = this.progression.map(chord => ({
            ...chord,
            root: this.transposeNoteName(chord.root, semitones)
        }));

        this.recomputeChordDegrees();
        this.syncAllUI();
        this.renderChords();
        this.restartIfPlaying();
        this.setStatus(`Transposed ${semitones > 0 ? '+' : ''}${semitones} semitone`, 1400);
    }

    progressionText() {
        return this.progression.map((chord) => {
            const ext = Array.isArray(chord.extensions) && chord.extensions.length > 0
                ? `(${chord.extensions.join(',')})`
                : '';
            return `${chord.root}${chord.quality}${ext} x${chord.bars}`;
        }).join(' | ');
    }

    copyChordsToClipboard() {
        const text = this.progressionText();
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(text).then(() => {
                this.setStatus('Chord progression copied', 1500);
            }).catch(() => {
                window.prompt('Copy this progression:', text);
                this.setStatus('Chord progression ready to copy', 1500);
            });
            return;
        }

        window.prompt('Copy this progression:', text);
        this.setStatus('Chord progression ready to copy', 1500);
    }

    // ========================================
    // THEORY UTILITIES
    // ========================================
    getRomanNumeral(chord) {
        const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];
        const degree = chord.degree - 1;
        let roman = romanNumerals[degree] || '?';

        const q = chord.quality.toLowerCase();
        if (q.includes('min') || q.includes('m7') || q.includes('ø')) {
            roman = roman.toLowerCase();
        }
        if (q.includes('dim') || q.includes('ø')) roman += '°';

        return roman;
    }

    getSuggestedScale(chord) {
        const q = chord.quality.toLowerCase();
        const root = chord.root;

        if (q.includes('maj7')) return `${root} Lydian`;
        if (q.includes('m7')) {
            if (chord.degree === 2) return `${root} Dorian`;
            if (chord.degree === 3) return `${root} Phrygian`;
            if (chord.degree === 6) return `${root} Aeolian`;
            return `${root} Minor`;
        }
        if (q.includes('dom')) return `${root} Mixolydian`;
        if (q.includes('ø')) return `${root} Locrian`;

        return `${root} Pentatonic`;
    }

    // ========================================
    // STUDIO CHALLENGE
    // ========================================
    getStudioChallenge() {
        const challenges = [
            'Use only the top two strings when you solo over this progression.',
            'Play every chord tone as a single-note melody line before improvising.',
            'Limit yourself to three different pitches per bar.',
            'Play only in the upper octave of your instrument.',
            'Use only quarter notes — no eighth notes or triplets.',
            'Start every phrase on beat 3 instead of beat 1.',
            'End every phrase with a rest — silence is part of the music.',
            'Use only downstrokes for the duration of this track.',
            'Play the progression in double time (twice as fast).',
            'Use dynamics only — no bends or vibrato this round.',
            'Target only the 3rd and 7th of each chord when soloing.',
            'Transpose your ideas up an octave every 4 bars.',
            'Play a motif in bar 1 and develop it for the next 3 bars.',
            'Use the relative mode — if the key is major, solo in its relative minor.',
            'Stay strictly diatonic — no chromatic passing tones.',
            'Play every note staccato.',
            'Start each phrase on an off-beat (the "and" of a beat).',
            'Use at least one rest longer than a full bar in your improvisation.'
        ];

        const challenge = challenges[Math.floor(Math.random() * challenges.length)];

        const banner = document.getElementById('ChallengeBanner');
        const text = document.getElementById('ChallengeText');

        if (banner && text) {
            text.textContent = challenge;
            banner.style.display = 'flex';
        }

        this.setStatus('Challenge loaded', 1400);
    }

}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
    window.flRunWhenPrivacyReady(() => {
        const engine = new GeneratorEngineV20();
        engine.init();
        window.genApp = engine;
    });
});
